import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  Badge,
  SimpleGrid,
  HStack,
  VStack,
  Heading,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Flex,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  List,
  ListItem,
  ListIcon,
  Icon,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  AspectRatio,
  Spinner,
  Image,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";
import { buildScenarioDownloadFilename } from "../lib/scenarioNaming";
import { 
  FaExclamationTriangle, 
  FaHeartbeat, 
  FaLungs, 
  FaBrain,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaUser,
  FaWind,
  FaHelicopter,
  FaPrint,
  FaHospital,
  FaImage,
  FaVideo,
  FaSpinner,
  FaExclamationCircle
} from 'react-icons/fa';

const printStyles = `
  /* Page Break Utilities */
  .page-break-before {
    page-break-before: always !important;
    break-before: page !important;
  }
  
  .page-break-after {
    page-break-after: always !important;
    break-after: page !important;
  }
  
  .page-break-avoid {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }

  /* Simple Print Mode - ON SCREEN - Fix positioning and sizing */
  body.simple-print .no-print {
    display: none !important;
    visibility: hidden !important;
    position: absolute !important;
    left: -99999px !important;
    height: 0 !important;
    overflow: hidden !important;
  }
  
  body.simple-print {
    --chakra-colors-text: #000 !important;
    --chakra-colors-gray-900: #000 !important;
    --chakra-colors-gray-800: #000 !important;
    --chakra-colors-gray-700: #000 !important;
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    position: relative !important;
    top: 0 !important;
    left: 0 !important;
  }
  
  /* Remove any container backgrounds */
  body.simple-print #root,
  body.simple-print main,
  body.simple-print > div {
    background: white !important;
    background-color: white !important;
    margin: 0 !important;
    padding: 8px !important;
  }
  
  body.simple-print .chakra-card,
  body.simple-print .chakra-card *,
  body.simple-print .chakra-text,
  body.simple-print .chakra-heading,
  body.simple-print .chakra-badge,
  body.simple-print .chakra-stat,
  body.simple-print .chakra-list,
  body.simple-print .chakra-box > * {
    color: #000 !important;
    line-height: 1.0 !important;
  }

  body.simple-print .chakra-text,
  body.simple-print p,
  body.simple-print div,
  body.simple-print span {
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    hyphens: auto !important;
    max-width: 100% !important;
  }

  body.simple-print .chakra-card,
  body.simple-print .chakra-card * {
    background: white !important;
    box-shadow: none !important;
    font-size: 5pt !important;
  }
  
  /* Watermark for simple print - visible on screen and in print */
  body.simple-print .print-watermark {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) rotate(-45deg) !important;
    font-size: 96px !important;
    font-weight: bold !important;
    opacity: 0.08 !important;
    color: #000 !important;
    pointer-events: none !important;
    user-select: none !important;
    z-index: 9998 !important;
    white-space: nowrap !important;
  }
  
  /* Force black text on all text elements */
  body.simple-print .chakra-text,
  body.simple-print .chakra-heading,
  body.simple-print .chakra-stat__number,
  body.simple-print .chakra-stat__label,
  body.simple-print p,
  body.simple-print span,
  body.simple-print div,
  body.simple-print td,
  body.simple-print th,
  body.simple-print li,
  body.simple-print h1,
  body.simple-print h2,
  body.simple-print h3,
  body.simple-print h4,
  body.simple-print h5,
  body.simple-print h6,
  body.simple-print label,
  body.simple-print a {
    color: #000 !important;
  }
  
  body.simple-print .chakra-card {
    border: 1px solid #000 !important;
    padding: 0.5px !important;
    margin: 1px 0 !important;
    page-break-inside: avoid;
  }
  
  body.simple-print .chakra-card__header {
    border-bottom: 1px solid #000 !important;
    padding: 1px 2px !important;
    font-size: 6pt !important;
    font-weight: bold !important;
    background: white !important;
  }
  
  body.simple-print .chakra-card__body {
    padding: 1.5px !important;
  }
  
  body.simple-print .chakra-badge {
    border: 1px solid #000 !important;
    padding: 0 1px !important;
    display: inline-block !important;
    font-size: 5pt !important;
    background: white !important;
    max-width: 100% !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    white-space: normal !important;
    line-height: 1.1 !important;
  }
  
  body.simple-print .chakra-heading {
    font-size: 6pt !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  body.simple-print .chakra-text {
    font-size: 5pt !important;
    line-height: 1.0 !important;
    margin: 0 !important;
  }
  
  body.simple-print .vital-sign-box,
  body.simple-print .chakra-stat {
    padding: 1px 2px !important;
    font-size: 4pt !important;
  }
  
  body.simple-print .chakra-stat__number {
    font-size: 5pt !important;
  }
  
  body.simple-print .chakra-stat__label {
    font-size: 4pt !important;
  }
  
  body.simple-print .chakra-stack > *,
  body.simple-print .chakra-vstack > *,
  body.simple-print .chakra-hstack > * {
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1.0 !important;
  }
  
  body.simple-print .chakra-list {
    margin: 0 !important;
    padding-left: 8px !important;
    line-height: 1.0 !important;
  }
  
  body.simple-print .chakra-list__item {
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1.0 !important;
  }
  
  /* Ultra-compact boxes in simple print */
  body.simple-print .chakra-box {
    padding: 1px !important;
    margin: 0.5px 0 !important;
  }
  
  body.simple-print .interventions-grid .chakra-card__body,
  body.simple-print .critical-actions-list,
  body.simple-print .common-pitfalls-list,
  body.simple-print .differential-diagnosis-list {
    padding: 0px !important;
    margin: 0px !important;
    line-height: 1.1 !important;
  }
  
  body.simple-print .chakra-list .chakra-list__item {
    margin-bottom: 0 !important;
    padding: 0 !important;
  }
  
  body.simple-print svg {
    display: none !important;
  }
  
  /* Simple print uses natural single-column flow - NO FORCED GRIDS */
  body.simple-print .chakra-simple-grid {
    display: block !important;
  }
  
  body.simple-print .chakra-simple-grid > * {
    display: block !important;
    width: 100% !important;
  }
  
  /* Show print buttons in simple print mode */
  body.simple-print .print-button {
    display: inline-flex !important;
    visibility: visible !important;
    position: fixed !important;
    top: 8px !important;
    right: 8px !important;
    z-index: 9999 !important;
    opacity: 0.95 !important;
  }

  body.simple-print .print-button button {
    color: #000 !important;
    background: #FFFFFF !important;
    border: 3px solid #000 !important;
    padding: 8px 16px !important;
    font-size: 12pt !important;
    font-weight: bold !important;
    cursor: pointer !important;
    border-radius: 4px !important;
  }

  body.simple-print .print-button button:hover {
    background: #f0f0f0 !important;
  }

  /* Hide print buttons when actually printing */
  @media print {
    body.simple-print .print-button {
      display: none !important;
      visibility: hidden !important;
    }
  }

  /* Back button in simple print mode - visible on screen, hidden when printing */
  body.simple-print .back-button {
    display: inline-flex !important;
    visibility: visible !important;
    position: fixed !important;
    top: 8px !important;
    left: 8px !important;
    z-index: 9999 !important;
    background: #FFFFFF !important;
    color: #000 !important;
    border: 3px solid #000 !important;
    padding: 8px 16px !important;
    font-size: 12pt !important;
    font-weight: bold !important;
    cursor: pointer !important;
    border-radius: 4px !important;
  }

  body.simple-print .back-button::before {
    content: "← " !important;
  }

  body.simple-print .back-button:hover {
    background: #f0f0f0 !important;
  }

  /* Hide back button when actually printing */
  @media print {
    body.simple-print .back-button {
      display: none !important;
      visibility: hidden !important;
      position: absolute !important;
      left: -99999px !important;
    }
  }

  /* Hide mode selector and header in tablet view - NUCLEAR OPTION */
  body.tablet-view .no-print,
  body.tablet-view.portrait .no-print,
  body.tablet-view.landscape .no-print {
    display: none !important;
    visibility: hidden !important;
    position: absolute !important;
    left: -99999px !important;
    height: 0 !important;
    overflow: hidden !important;
  }
  
  body.tablet-view div.no-print,
  body.tablet-view .chakra-stack.no-print,
  body.tablet-view .css-swaqeq {
    display: none !important;
    visibility: hidden !important;
    position: absolute !important;
    left: -99999px !important;
    top: -99999px !important;
    width: 0 !important;
    height: 0 !important;
    max-height: 0 !important;
    overflow: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  /* EXCEPTION: Show evaluation checkboxes even in tablet/print modes */
  body.tablet-view input[type="checkbox"],
  body.tablet-view.portrait input[type="checkbox"],
  body.tablet-view.landscape input[type="checkbox"] {
    display: inline-block !important;
    visibility: visible !important;
    opacity: 1 !important;
    pointer-events: auto !important;
    width: 16px !important;
    height: 16px !important;
    margin-right: 8px !important;
    cursor: pointer !important;
  }

  /* Also ensure checkboxes are visible in simple print mode for evaluation */
  body.simple-print input[type="checkbox"] {
    display: inline-block !important;
    visibility: visible !important;
    -webkit-appearance: checkbox !important;
    appearance: checkbox !important;
    width: 12px !important;
    height: 12px !important;
    margin-right: 4px !important;
    print-color-adjust: exact !important;
  }

  /* CRITICAL: Hide ALL form inputs and selectors in tablet view */
  body.tablet-view input,
  body.tablet-view select,
  body.tablet-view textarea,
  body.tablet-view button:not(.tablet-controls button),
  body.tablet-view form {
    display: none !important;
    visibility: hidden !important;
  }

  body.tablet-view.portrait input,
  body.tablet-view.portrait select,
  body.tablet-view.portrait textarea,
  body.tablet-view.portrait button:not(.tablet-controls button),
  body.tablet-view.portrait form {
    display: none !important;
    visibility: hidden !important;
  }

  body.tablet-view.landscape input,
  body.tablet-view.landscape select,
  body.tablet-view.landscape textarea,
  body.tablet-view.landscape button:not(.tablet-controls button),
  body.tablet-view.landscape form {
    display: none !important;
    visibility: hidden !important;
  }

  body.tablet-view .scenario-header,
  body.tablet-view.portrait .scenario-header,
  body.tablet-view.landscape .scenario-header,
  body.tablet-view div.scenario-header,
  body.tablet-view .avoid-break.scenario-header,
  body.tablet-view .css-15rkey4 {
    display: none !important;
    visibility: hidden !important;
    position: absolute !important;
    left: -99999px !important;
    top: -99999px !important;
    width: 0 !important;
    height: 0 !important;
    max-height: 0 !important;
    overflow: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  /* Show tablet controls - NUCLEAR OPTION */
  body.tablet-view .tablet-controls,
  body.tablet-view.portrait .tablet-controls,
  body.tablet-view.landscape .tablet-controls,
  body.tablet-view div.tablet-controls,
  body.tablet-view .css-c8spic {
    display: block !important;
    visibility: visible !important;
    position: fixed !important;
    top: 16px !important;
    right: 16px !important;
    z-index: 99999 !important;
    opacity: 0.4 !important;
  }

  body.tablet-view .tablet-controls:hover,
  body.tablet-view.portrait .tablet-controls:hover,
  body.tablet-view.landscape .tablet-controls:hover {
    opacity: 0.95 !important;
  }

  /* Patient info banner - always visible */
  body.simple-print .patient-info-banner {
    display: block !important;
    padding: 2px !important;
    margin-bottom: 2px !important;
    border-bottom: 1px solid #000 !important;
    background: white !important;
  }

  body.simple-print .patient-info-banner .chakra-text {
    font-size: 6pt !important;
    font-weight: bold !important;
  }

  body.tablet-view .patient-info-banner {
    display: block !important;
    padding: 4px !important;
    margin-bottom: 4px !important;
  }

  body.tablet-view.portrait .patient-info-banner .chakra-text {
    font-size: 7pt !important;
  }

  body.tablet-view.landscape .patient-info-banner .chakra-text {
    font-size: 8pt !important;
  }

  /* Tablet View Mode - ON SCREEN - FULLSCREEN SCALED */
  /* Portrait: Matches simple print but with color - Fix field expansion */
  body.tablet-view.portrait {
    font-size: 7pt !important;
    line-height: 1 !important;
    overflow: auto !important;
    height: 100vh !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
  }
  
  body.tablet-view.portrait * {
    line-height: 1 !important;
    margin: 0 !important;
    padding: 0 !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    max-width: 100% !important;
    text-overflow: ellipsis !important;
    max-height: none !important;
    overflow-wrap: anywhere !important;
    hyphens: auto !important;
  }
  
  body.tablet-view.portrait .chakra-text,
  body.tablet-view.portrait p,
  body.tablet-view.portrait span,
  body.tablet-view.portrait div {
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    white-space: pre-wrap !important;
    max-width: 100% !important;
    overflow: hidden !important;
  }
  
  body.tablet-view.portrait .chakra-card {
    border: 1px solid #e2e8f0 !important;
    padding: 3px !important;
    margin: 2px 0 !important;
    page-break-inside: avoid;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .chakra-card__header {
    border-bottom: 1px solid !important;
    padding: 1px 2px !important;
    font-size: 7pt !important;
    font-weight: bold !important;
  }
  
  body.tablet-view.portrait .chakra-card__body {
    padding: 3px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .chakra-badge {
    border: 1px solid currentColor !important;
    padding: 0 2px !important;
    display: inline-block !important;
    font-size: 7pt !important;
    max-width: 100% !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    white-space: normal !important;
    line-height: 1.2 !important;
  }
  
  body.tablet-view.portrait .chakra-heading {
    font-size: 7pt !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  /* Badge wrapping in normal mode */
  .chakra-badge {
    max-width: 100% !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    white-space: normal !important;
  }
  
  body.tablet-view.portrait .chakra-text {
    font-size: 7pt !important;
    line-height: 1 !important;
    margin: 0 !important;
  }
  
  body.tablet-view.portrait .chakra-stack > * {
    margin: 0 !important;
    padding: 0 !important;
  }
  
  body.tablet-view.portrait .chakra-list {
    margin: 0 !important;
    padding-left: 10px !important;
  }
  
  body.tablet-view.portrait .chakra-list__item {
    margin: 0 !important;
    line-height: 1.1 !important;
  }
  
  body.tablet-view.portrait .chakra-stat__number {
    font-size: 7pt !important;
  }
  
  body.tablet-view.portrait .chakra-stat__label {
    font-size: 7pt !important;
  }
  
  /* Handle long unbound text in narrative fields */
  body.tablet-view.portrait .chakra-text,
  body.tablet-view.portrait .chakra-card__body p,
  body.tablet-view.portrait .chakra-card__body div,
  body.tablet-view.portrait .chakra-list__item {
    max-width: 100% !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    hyphens: auto !important;
    white-space: normal !important;
    overflow: hidden !important;
  }

  /* Specifically target intervention cards and prior to arrival */
  body.tablet-view.portrait .interventions-grid .chakra-card__body,
  body.tablet-view.portrait [class*="prior"] .chakra-text,
  body.tablet-view.portrait [class*="Prior"] .chakra-text {
    max-width: 100% !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    hyphens: auto !important;
    white-space: pre-wrap !important;
    overflow: hidden !important;
    display: block !important;
  }
  
  /* Landscape: wider spread layout */
  body.tablet-view.landscape {
    font-size: 8pt !important;
    line-height: 1.2 !important;
    overflow: auto !important;
  }
  
  body.tablet-view.landscape * {
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    max-width: 100% !important;
  }
  
  body.tablet-view.landscape .chakra-card {
    margin-bottom: 3px !important;
    padding: 3px !important;
  }
  
  body.tablet-view.landscape .chakra-card__header {
    padding: 3px 5px !important;
    font-size: 8pt !important;
  }
  
  body.tablet-view.landscape .chakra-card__body {
    padding: 5px !important;
  }
  
  body.tablet-view.landscape .chakra-text {
    font-size: 8pt !important;
    line-height: 1.2 !important;
  }
  
  body.tablet-view.landscape .chakra-stat__number {
    font-size: 8pt !important;
  }
  
  body.tablet-view.landscape .chakra-stat__label {
    font-size: 8pt !important;
  }
  
  /* Handle long unbound text in landscape */
  body.tablet-view.landscape .chakra-text,
  body.tablet-view.landscape .chakra-card__body p,
  body.tablet-view.landscape .chakra-card__body div,
  body.tablet-view.landscape .chakra-list__item {
    max-width: 100% !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
    hyphens: auto !important;
    overflow: hidden !important;
  }
  
  /* Landscape: wider grids */
  body.tablet-view.landscape .first-row-grid {
    grid-template-columns: 1fr 1fr 1fr !important;
  }
  
  body.tablet-view.landscape .second-row-grid {
    grid-template-columns: 1fr 1fr 1fr !important;
  }
  
  body.tablet-view.landscape .interventions-grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr 1fr 1fr !important;
  }
  
  /* CRITICAL: Force grid layouts in portrait tablet mode - MUST match simple print exactly - Fix expansion */
  body.tablet-view.portrait .first-row-grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 3px !important;
    column-gap: 4px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .first-row-grid > * {
    display: block !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .chakra-simple-grid.first-row-grid {
    display: grid !important;
  }
  
  body.tablet-view.portrait .second-row-grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 3px !important;
    column-gap: 4px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .second-row-grid > * {
    display: block !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .chakra-simple-grid.second-row-grid {
    display: grid !important;
  }
  
  body.tablet-view.portrait .trending-row-grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 3px !important;
    column-gap: 4px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .trending-row-grid > * {
    display: block !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .chakra-simple-grid.trending-row-grid {
    display: grid !important;
  }
  
  body.tablet-view.portrait .interventions-grid,
  body.tablet-view.portrait .critical-actions-list,
  body.tablet-view.portrait .common-pitfalls-list,
  body.tablet-view.portrait .differential-diagnosis-list {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 3px !important;
    column-gap: 4px !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .interventions-grid > *,
  body.tablet-view.portrait .critical-actions-list > *,
  body.tablet-view.portrait .common-pitfalls-list > *,
  body.tablet-view.portrait .differential-diagnosis-list > * {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
    display: block !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  
  body.tablet-view.portrait .chakra-stack.interventions-grid,
  body.tablet-view.portrait .chakra-list.critical-actions-list,
  body.tablet-view.portrait .chakra-list.common-pitfalls-list,
  body.tablet-view.portrait .chakra-list.differential-diagnosis-list {
    display: grid !important;
  }
  
  body.tablet-view .chakra-card {
    page-break-inside: avoid;
  }
  
  /* Fullscreen scaling for tablet view */
  body.tablet-view:fullscreen {
    width: 100vw;
    height: 100vh;
    overflow: auto;
    background: white;
  }
  
  body.tablet-view:-webkit-full-screen {
    width: 100vw;
    height: 100vh;
    overflow: auto;
    background: white;
  }
  
  body.tablet-view:-moz-full-screen {
    width: 100vw;
    height: 100vh;
    overflow: auto;
    background: white;
  }
  
  body.tablet-view:-ms-fullscreen {
    width: 100vw;
    height: 100vh;
    overflow: auto;
    background: white;
  }
  
  /* Accessible View Mode - WCAG AA Compliant */
  body.accessible {
    background: white !important;
    color: #000 !important;
    font-family: Arial, sans-serif !important;
    font-size: 14pt !important;
    line-height: 1.5 !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 20px !important;
    overflow: visible !important;
    overflow-x: visible !important;
  }

  /* CRITICAL: Override Chakra UI's dark background theme */
  html.accessible,
  html body.accessible,
  body.accessible,
  body.accessible #root,
  body.accessible .chakra-ui-light,
  body.accessible .chakra-ui-dark,
  body.accessible [data-theme],
  body.accessible main,
  body.accessible > div,
  body.accessible div#root {
    background: white !important;
    background-color: white !important;
    background-image: none !important;
  }

  /* Remove scrollbar styles - not needed for accessible view */
  /*
  body.accessible::-webkit-scrollbar {
    width: 12px !important;
  }

  body.accessible::-webkit-scrollbar-track {
    background: #f1f1f1 !important;
  }

  body.accessible::-webkit-scrollbar-thumb {
    background: #888 !important;
    border-radius: 6px !important;
  }

  body.accessible::-webkit-scrollbar-thumb:hover {
    background: #555 !important;
  }
  */

  body.accessible * {
    font-family: Arial, sans-serif !important;
    line-height: 1.5 !important;
  }

  body.accessible .no-print,
  body.accessible .patient-info-banner,
  body.accessible .scenario-header,
  body.accessible .chakra-card,
  body.accessible .chakra-badge,
  body.accessible .chakra-simple-grid,
  body.accessible svg,
  body.accessible .chakra-icon {
    display: none !important;
    visibility: hidden !important;
    position: absolute !important;
    left: -99999px !important;
    height: 0 !important;
    overflow: hidden !important;
  }

  body.accessible .accessible-content {
    display: block !important;
    visibility: visible !important;
    position: relative !important;
    left: auto !important;
    height: auto !important;
  }

  /* Force hide visual content in accessible mode */
  body.accessible .visual-content-wrapper {
    display: none !important;
    visibility: hidden !important;
    position: absolute !important;
    left: -99999px !important;
    height: 0 !important;
    overflow: hidden !important;
  }

  body.accessible h1 {
    font-size: 24pt !important;
    font-weight: bold !important;
    margin: 20px 0 10px 0 !important;
    color: #000 !important;
  }

  body.accessible h2 {
    font-size: 18pt !important;
    font-weight: bold !important;
    margin: 15px 0 8px 0 !important;
    color: #000 !important;
  }

  body.accessible h3 {
    font-size: 14pt !important;
    font-weight: bold !important;
    margin: 10px 0 5px 0 !important;
    color: #000 !important;
  }

  body.accessible p {
    margin: 8px 0 !important;
    color: #000 !important;
    font-size: 14pt !important;
  }

  body.accessible ul,
  body.accessible ol {
    margin: 10px 0 10px 20px !important;
    padding-left: 20px !important;
  }

  body.accessible li {
    margin: 5px 0 !important;
    color: #000 !important;
    font-size: 14pt !important;
  }

  body.accessible strong {
    font-weight: bold !important;
    color: #000 !important;
  }

  /* High Contrast Mode - White on Black */
  body.accessible.high-contrast {
    background: #000 !important;
    color: #fff !important;
  }

  body.accessible.high-contrast h1,
  body.accessible.high-contrast h2,
  body.accessible.high-contrast h3,
  body.accessible.high-contrast p,
  body.accessible.high-contrast li,
  body.accessible.high-contrast strong {
    color: #fff !important;
  }

  body.accessible.high-contrast .accessible-content {
    background: #000 !important;
    color: #fff !important;
  }

  /* High-contrast overrides for containers forced white by lines above */
  body.accessible.high-contrast #root,
  body.accessible.high-contrast .chakra-ui-light,
  body.accessible.high-contrast .chakra-ui-dark,
  body.accessible.high-contrast [data-theme],
  body.accessible.high-contrast main,
  body.accessible.high-contrast > div,
  body.accessible.high-contrast div#root {
    background: #000 !important;
    background-color: #000 !important;
    background-image: none !important;
  }

  body.accessible .sr-only {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0,0,0,0) !important;
    white-space: nowrap !important;
    border-width: 0 !important;
  }

  /* Keep controls visible in accessible mode on screen, hide when printing */
  body.accessible .accessible-controls {
    display: block !important;
    position: sticky !important;
    top: 0 !important;
    background: inherit !important;
    z-index: 1000 !important;
    padding: 10px !important;
    border-bottom: 2px solid currentColor !important;
  }

  /* Accessible View - Print as actual text, not image */
  @media print {
    body.accessible,
    body.accessible.high-contrast {
      background: white !important;
      color: #000 !important;
      font-family: Arial, sans-serif !important;
      font-size: 12pt !important;
      line-height: 1.6 !important;
      margin: 0.75in !important;
      padding: 0 !important;
      /* Force text rendering, disable any graphics */
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    
    body.accessible *,
    body.accessible.high-contrast *,
    body.accessible #root,
    body.accessible > div,
    body.accessible main,
    body.accessible [class*="chakra"],
    body.accessible [class*="css-"] {
      background: white !important;
      background-color: white !important;
      background-image: none !important;
      color: #000 !important;
      box-shadow: none !important;
      border: none !important;
    }
    
    /* Hide ALL non-text elements in print */
    body.accessible canvas,
    body.accessible img,
    body.accessible svg,
    body.accessible video,
    body.accessible.high-contrast canvas,
    body.accessible.high-contrast img,
    body.accessible.high-contrast svg,
    body.accessible.high-contrast video {
      display: none !important;
      visibility: hidden !important;
    }
    
    body.accessible .accessible-content,
    body.accessible.high-contrast .accessible-content {
      display: block !important;
      background: transparent !important;
      max-width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    /* Headings */
    body.accessible h1,
    body.accessible.high-contrast h1 {
      font-size: 20pt !important;
      font-weight: bold !important;
      margin: 16pt 0 12pt 0 !important;
      page-break-after: avoid !important;
    }
    
    body.accessible h2,
    body.accessible.high-contrast h2 {
      font-size: 16pt !important;
      font-weight: bold !important;
      margin: 14pt 0 10pt 0 !important;
      page-break-after: avoid !important;
    }
    
    body.accessible h3,
    body.accessible.high-contrast h3 {
      font-size: 13pt !important;
      font-weight: bold !important;
      margin: 12pt 0 8pt 0 !important;
      page-break-after: avoid !important;
    }
    
    /* Body text */
    body.accessible p,
    body.accessible.high-contrast p {
      margin: 8pt 0 !important;
      font-size: 12pt !important;
      line-height: 1.6 !important;
    }
    
    /* Lists */
    body.accessible ul,
    body.accessible.high-contrast ul,
    body.accessible ol,
    body.accessible.high-contrast ol {
      margin: 8pt 0 !important;
      padding-left: 24pt !important;
    }
    
    body.accessible li,
    body.accessible.high-contrast li {
      margin: 4pt 0 !important;
      font-size: 12pt !important;
      line-height: 1.6 !important;
    }
    
    body.accessible strong,
    body.accessible.high-contrast strong {
      font-weight: bold !important;
    }
    
    /* Hide controls when printing */
    body.accessible .accessible-controls,
    body.accessible.high-contrast .accessible-controls,
    body.accessible .no-print,
    body.accessible.high-contrast .no-print {
      display: none !important;
    }
    
    @page {
      size: letter;
      margin: 0.75in;
    }
  }
  
  /* Conditional page break before differentials in simple print */
  body.simple-print .page-break-before-differentials {
    /* Only apply page break if .force-page-break class is present */
    display: block !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  body.simple-print .page-break-before-differentials.force-page-break {
    page-break-before: always !important;
    break-before: page !important;
  }
  
  /* Two-page variant for simple print */
  body.simple-print.two-page .page-break-after-demographics {
    page-break-after: always !important;
    break-after: page !important;
  }

  /* Two-page tablet mode: page break after interventions ONLY */
  body.two-page-mode .interventions-card {
    page-break-after: always !important;
    break-after: page !important;
  }

  /* Two-Page Print Mode - Fix layout */
  body.tablet-view.two-page-mode {
    background: white !important;
  }

  body.tablet-view.two-page-mode #root,
  body.tablet-view.two-page-mode main,
  body.tablet-view.two-page-mode > div {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* PRINT MEDIA QUERIES - only applies when actually printing */
  @media print {
    /* Preserve tablet view classes during print */
    body.tablet-view.portrait,
    body.tablet-view.landscape {
      /* Keep all tablet view styles during print */
    }
    
    /* Hide only the controls when printing from tablet view */
    body.tablet-view .tablet-controls {
      display: none !important;
      visibility: hidden !important;
    }
    
    /* Two-page mode print fixes */
    body.tablet-view.two-page-mode {
      font-size: 8pt !important;
    }
    
    body.tablet-view.two-page-mode::before {
      content: none !important;
      display: none !important;
    }
    
    body.tablet-view.two-page-mode .scenario-content-for-pdf {
      page-break-before: avoid !important;
      margin-top: 0 !important;
      padding-top: 0 !important;
    }
    
    body.tablet-view.two-page-mode .page-break-before-differentials {
      page-break-before: always !important;
      break-before: page !important;
    }
    
    body.tablet-view.two-page-mode .no-print,
    body.tablet-view.two-page-mode .tablet-controls {
      display: none !important;
    }
    
    @page {
      size: letter;
      margin: 0.15in;
    }
    
    * {
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
    }
    
    html:not(.color-print),
    body:not(.color-print),
    body:not(.color-print) *:not(.chakra-card__header) {
      background: white !important;
      background-color: white !important;
    }

    .no-print {
      display: none !important;
    }

    .scenario-header {
      display: none !important;
    }

    .chakra-card {
      box-shadow: none !important;
      border: 1px solid #e2e8f0 !important;
      page-break-inside: avoid;
    }

    body:not(.simple-print):not(.tablet-view):not(.color-print) .chakra-card__header {
      background-color: #f7fafc !important;
      color: #2d3748 !important;
      border-bottom: 1px solid #e2e8f0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    
    .chakra-badge {
      border: 1px solid currentColor;
    }
    
    .avoid-break {
      page-break-inside: avoid;
    }
    
    .page-break-before {
      page-break-before: always;
    }
    
    /* Simple Print Mode - ULTRA CONDENSED FOR ONE PAGE - Fix positioning */
    body.simple-print {
      margin: 0 !important;
      padding: 0 !important;
    }
    
    body.simple-print * {
      background: white !important;
      color: black !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
      line-height: 0.9 !important;
      font-size: 4pt !important;
    }
    
    /* Hide all UI controls when printing */
    body.simple-print .print-button,
    body.simple-print .back-button,
    body.simple-print .no-print {
      display: none !important;
    }
    
    body.simple-print .chakra-card {
      border: 1px solid #000 !important;
      padding: 0.5px !important;
      margin: 0.5px 0 !important;
      page-break-inside: avoid;
    }
    
    body.simple-print .chakra-card__header {
      border-bottom: 1px solid #000 !important;
      padding: 0px 0.5px !important;
      font-size: 5pt !important;
      font-weight: bold !important;
    }
    
    body.simple-print .chakra-card__body {
      padding: 0.5px !important;
    }
    
    body.simple-print .chakra-heading {
      font-size: 5pt !important;
    }
    
    body.simple-print .chakra-text {
      font-size: 4pt !important;
    }
    
    body.simple-print .chakra-stat__label {
      font-size: 3pt !important;
    }
    
    body.simple-print .chakra-stat__number {
      font-size: 4pt !important;
    }
    
    body.simple-print .chakra-stack > * {
      margin: 0 !important;
      padding: 0 !important;
    }
    
    body.simple-print .chakra-list {
      margin: 0 !important;
      padding-left: 6px !important;
    }
    
    body.simple-print .chakra-list__item {
      margin: 0 !important;
      line-height: 0.9 !important;
    }
    
    body.simple-print @page {
      size: letter;
      margin: 0.15in;
    }
    
    /* Ultra-compress preceptor notes and scenario flow in print */
    body.simple-print .preceptor-notes-section .chakra-card__body,
    body.simple-print .scenario-flow-section .chakra-card__body {
      padding: 0.25px !important;
      line-height: 0.85 !important;
    }
    
    body.simple-print .preceptor-notes-section .chakra-text,
    body.simple-print .scenario-flow-section .chakra-text {
      font-size: 3.5pt !important;
      line-height: 0.85 !important;
      margin: 0 !important;
    }
    
    body.simple-print .preceptor-notes-section .chakra-heading,
    body.simple-print .scenario-flow-section .chakra-heading {
      font-size: 4.5pt !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    /* Watermark in print */
    body.simple-print .print-watermark {
      position: fixed !important;
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%) rotate(-45deg) !important;
      font-size: 96px !important;
      font-weight: bold !important;
      opacity: 0.08 !important;
      color: #000 !important;
      pointer-events: none !important;
      user-select: none !important;
      z-index: 9998 !important;
      white-space: nowrap !important;
    }
    
    /* Page break utilities in print */
    body.simple-print .page-break-avoid {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    
    body.simple-print .page-break-after {
      page-break-after: always !important;
      break-after: page !important;
    }
    
    body.simple-print .page-break-before {
      page-break-before: always !important;
      break-before: page !important;
    }
    
    body.simple-print .page-break {
      display: none !important;
    }
    
    /* Hide icons in simple print */
    body.simple-print svg {
      display: none !important;
    }
    
    /* Force side-by-side layout for first row in simple print */
    body.simple-print .first-row-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.simple-print .first-row-grid > * {
      display: block !important;
    }
    
    /* Override Chakra's SimpleGrid in simple print */
    body.simple-print .chakra-simple-grid.first-row-grid {
      display: grid !important;
    }
    
    /* Force side-by-side layout for second row (Primary + Secondary Assessment) in simple print */
    body.simple-print .second-row-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.simple-print .second-row-grid > * {
      display: block !important;
    }
    
    body.simple-print .chakra-simple-grid.second-row-grid {
      display: grid !important;
    }
    
    /* Force side-by-side layout for trending vitals row in simple print */
    body.simple-print .trending-row-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.simple-print .trending-row-grid > * {
      display: block !important;
    }
    
    body.simple-print .chakra-simple-grid.trending-row-grid {
      display: grid !important;
    }
    
    /* Force 2-column grid layout for interventions and educational content */
    body.simple-print .interventions-grid,
    body.simple-print .critical-actions-list,
    body.simple-print .common-pitfalls-list,
    body.simple-print .differential-diagnosis-list {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.simple-print .interventions-grid > *,
    body.simple-print .critical-actions-list > *,
    body.simple-print .common-pitfalls-list > *,
    body.simple-print .differential-diagnosis-list > * {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      display: block !important;
    }
    
    /* Override Chakra's flex layout */
    body.simple-print .chakra-stack.interventions-grid,
    body.simple-print .chakra-list.critical-actions-list,
    body.simple-print .chakra-list.common-pitfalls-list,
    body.simple-print .chakra-list.differential-diagnosis-list {
      display: grid !important;
    }
    
    /* Color Print Mode - preserve card colors and readable fonts */
    body.color-print {
      background: white !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    body.color-print #root,
    body.color-print main,
    body.color-print > div {
      background: white !important;
      background-color: white !important;
    }

    body.color-print .no-print,
    body.color-print .scenario-header {
      display: none !important;
    }

    body.color-print * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    body.color-print .chakra-card {
      box-shadow: none !important;
      border: 1px solid #e2e8f0 !important;
      page-break-inside: avoid !important;
    }

    body.color-print .chakra-card__header {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    body.color-print .chakra-badge {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      border: 1px solid currentColor !important;
    }

    body.color-print .chakra-text,
    body.color-print .chakra-heading,
    body.color-print p,
    body.color-print li,
    body.color-print span {
      font-size: inherit !important;
    }

    body.color-print .chakra-card__body {
      font-size: 10pt !important;
    }

    body.color-print .chakra-card__header {
      font-size: 11pt !important;
    }

    body.color-print .chakra-stat__label {
      font-size: 8pt !important;
    }

    body.color-print .chakra-stat__number {
      font-size: 10pt !important;
    }

    /* Hide mode selector and header in tablet view - FORCE HIDE */
    body.tablet-view .no-print {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      overflow: hidden !important;
    }
    
    body.tablet-view .scenario-header {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      overflow: hidden !important;
    }
    
    /* Show tablet controls */
    body.tablet-view .tablet-controls {
      display: block !important;
      visibility: visible !important;
    }
    
    /* Tablet View Mode - PORTRAIT - Matches simple print but with color */
    body.tablet-view.portrait {
      font-size: 7pt !important;
      line-height: 1 !important;
      overflow: auto !important;
      height: 100vh !important;
    }
    
    body.tablet-view.portrait * {
      line-height: 1 !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    body.tablet-view.portrait .chakra-card {
      border: 1px solid #e2e8f0 !important;
      padding: 2px !important;
      margin: 1px 0 !important;
      page-break-inside: avoid;
    }
    
    body.tablet-view.portrait .chakra-card__header {
      border-bottom: 1px solid !important;
      padding: 1px 2px !important;
      font-size: 8pt !important;
      font-weight: bold !important;
    }
    
    body.tablet-view.portrait .chakra-card__body {
      padding: 2px !important;
    }
    
    body.tablet-view.portrait .chakra-badge {
      border: 1px solid currentColor !important;
      padding: 0 2px !important;
      display: inline-block;
      font-size: 6pt !important;
    }
    
    body.tablet-view.portrait .chakra-heading {
      font-size: 8pt !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    body.tablet-view.portrait .chakra-text {
      font-size: 7pt !important;
      line-height: 1 !important;
      margin: 0 !important;
    }
    
    body.tablet-view.portrait .chakra-stack > * {
      margin: 0 !important;
      padding: 0 !important;
    }
    
    body.tablet-view.portrait .chakra-list {
      margin: 0 !important;
      padding-left: 10px !important;
    }
    
    body.tablet-view.portrait .chakra-list__item {
      margin: 0 !important;
      line-height: 1.1 !important;
    }
    
    body.tablet-view.portrait .chakra-stat__number {
      font-size: 7pt !important;
    }
    
    body.tablet-view.portrait .chakra-stat__label {
      font-size: 6pt !important;
    }
    
    /* Tablet View Mode - LANDSCAPE - Wider spread layout */
    body.tablet-view.landscape {
      font-size: 8pt !important;
      line-height: 1.2 !important;
      overflow: auto !important;
    }
    
    body.tablet-view.landscape .chakra-card {
      margin-bottom: 3px !important;
      padding: 3px !important;
    }
    
    body.tablet-view.landscape .chakra-card__header {
      padding: 3px 5px !important;
      font-size: 10pt !important;
    }
    
    body.tablet-view.landscape .chakra-card__body {
      padding: 5px !important;
    }
    
    body.tablet-view.landscape .chakra-text {
      font-size: 8pt !important;
      line-height: 1.2 !important;
    }
    
    body.tablet-view.landscape .chakra-stat__number {
      font-size: 9pt !important;
    }
    
    body.tablet-view.landscape .chakra-stat__label {
      font-size: 7pt !important;
    }
    
    /* Landscape: wider grids */
    body.tablet-view.landscape .first-row-grid {
      grid-template-columns: 1fr 1fr 1fr !important;
    }
    
    body.tablet-view.landscape .second-row-grid {
      grid-template-columns: 1fr 1fr 1fr !important;
    }
    
    body.tablet-view.landscape .interventions-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr 1fr 1fr !important;
    }
    
    /* CRITICAL: Force grid layouts in portrait tablet mode - MUST match simple print exactly */
    body.tablet-view.portrait .first-row-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.tablet-view.portrait .first-row-grid > * {
      display: block !important;
    }
    
    body.tablet-view.portrait .chakra-simple-grid.first-row-grid {
      display: grid !important;
    }
    
    body.tablet-view.portrait .second-row-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.tablet-view.portrait .second-row-grid > * {
      display: block !important;
    }
    
    body.tablet-view.portrait .chakra-simple-grid.second-row-grid {
      display: grid !important;
    }
    
    body.tablet-view.portrait .trending-row-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.tablet-view.portrait .trending-row-grid > * {
      display: block !important;
    }
    
    body.tablet-view.portrait .chakra-simple-grid.trending-row-grid {
      display: grid !important;
    }
    
    body.tablet-view.portrait .interventions-grid,
    body.tablet-view.portrait .critical-actions-list,
    body.tablet-view.portrait .common-pitfalls-list,
    body.tablet-view.portrait .differential-diagnosis-list {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 2px !important;
      column-gap: 4px !important;
    }
    
    body.tablet-view.portrait .interventions-grid > *,
    body.tablet-view.portrait .critical-actions-list > *,
    body.tablet-view.portrait .common-pitfalls-list > *,
    body.tablet-view.portrait .differential-diagnosis-list > * {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      display: block !important;
    }
    
    body.tablet-view.portrait .chakra-stack.interventions-grid,
    body.tablet-view.portrait .chakra-list.critical-actions-list,
    body.tablet-view.portrait .chakra-list.common-pitfalls-list,
    body.tablet-view.portrait .chakra-list.differential-diagnosis-list {
      display: grid !important;
    }
  }
`;

// Helper to check if value exists and is not N/A or Unknown
const hasValue = (value) => {
  if (!value) return false;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    return normalized !== 'n/a' && normalized !== 'unknown' && normalized !== '';
  }
  return true;
};

// Checkable item wrapper component for evaluation mode
const CheckableItem = ({ children, isChecked, onToggle, label, evaluationMode }) => {
  if (!evaluationMode) {
    return children;
  }

  return (
    <HStack spacing={2} alignItems="flex-start">
      <Box
        as="input"
        type="checkbox"
        checked={isChecked}
        onChange={(e) => onToggle(e.target.checked)}
        mt={1}
        cursor="pointer"
        flexShrink={0}
      />
      <Box flex={1}>{children}</Box>
    </HStack>
  );
};

// Helper function to format vital signs with units
const formatVital = (vital) => {
  if (!vital) return null;
  if (typeof vital === 'object') {
    return vital.value ? `${vital.value} ${vital.unit || ''}`.trim() : null;
  }
  return vital;
};

// Helper to determine if vital is abnormal
const getVitalStatus = (type, value) => {
  if (!value) return 'normal';
  
  const numValue = typeof value === 'object' ? parseFloat(value.value) : parseFloat(value);
  if (isNaN(numValue)) return 'normal';

  const ranges = {
    hr: { critical: [numValue < 40 || numValue > 150], warning: [numValue < 60 || numValue > 120] },
    bp_systolic: { critical: [numValue < 90 || numValue > 180], warning: [numValue < 100 || numValue > 140] },
    rr: { critical: [numValue < 8 || numValue > 30], warning: [numValue < 12 || numValue > 24] },
    spo2: { critical: [numValue < 90], warning: [numValue < 94] },
    temp: { critical: [numValue < 95 || numValue > 104], warning: [numValue < 97 || numValue > 100.4] },
    bloodGlucose: { critical: [numValue < 60 || numValue > 300], warning: [numValue < 70 || numValue > 180] }
  };

  const range = ranges[type];
  if (!range) return 'normal';
  
  if (range.critical.some(condition => condition)) return 'critical';
  if (range.warning.some(condition => condition)) return 'warning';
  return 'normal';
};

// Get color for vital status
const getVitalColor = (status) => {
  switch(status) {
    case 'critical': return 'red.600';
    case 'warning': return 'orange.500';
    default: return 'green.600';
  }
};

// Get LOC color
const getLOCColor = (loc) => {
  if (!loc) return 'gray';
  const normalized = loc.toLowerCase();
  if (normalized.includes('alert') || normalized.includes('awake')) return 'green';
  if (normalized.includes('verbal') || normalized.includes('confused')) return 'yellow';
  if (normalized.includes('pain') || normalized.includes('unresponsive')) return 'red';
  return 'gray';
};

// Component to display generated media (images and videos)
const MediaDisplaySection = ({ scenario }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Check if any media exists or is being generated
  const hasMedia = scenario?.patientImageUrl || 
                   scenario?.sceneArrivalVideoUrl || 
                   scenario?.deteriorationVideoUrl || 
                   scenario?.improvementVideoUrl;
  
  const isGenerating = scenario?.mediaGenerationStatus === 'pending' || 
                      scenario?.mediaGenerationStatus === 'processing';
  
  const hasFailed = scenario?.mediaGenerationStatus === 'failed';
  
  if (!hasMedia && !isGenerating && !hasFailed && !scenario?.mediaGenerationEnabled) {
    return null; // Don't show section if media was never enabled
  }
  
  return (
    <Card 
      bg={cardBg} 
      borderColor={borderColor} 
      shadow="md" 
      borderRadius="lg"
      className="page-break-avoid"
      mb={4}
    >
      <CardHeader>
        <HStack spacing={3}>
          <Icon as={FaImage} boxSize={5} color="purple.400" />
          <Heading size="md">Generated Media</Heading>
          {isGenerating && (
            <Badge colorScheme="blue" display="flex" alignItems="center" gap={1}>
              <Spinner size="xs" />
              Generating...
            </Badge>
          )}
          {hasFailed && (
            <Badge colorScheme="red" display="flex" alignItems="center" gap={1}>
              <Icon as={FaExclamationCircle} />
              Generation Failed
            </Badge>
          )}
          {scenario?.mediaGenerationStatus === 'completed' && (
            <Badge colorScheme="green">Complete</Badge>
          )}
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* Patient Image */}
          {scenario?.patientImageUrl && (
            <Box>
              <Heading size="sm" mb={3}>Patient Image</Heading>
              <AspectRatio ratio={16/9} maxW="600px">
                <Image 
                  src={scenario.patientImageUrl} 
                  alt="Patient appearance"
                  borderRadius="md"
                  objectFit="cover"
                  fallback={
                    <Flex align="center" justify="center" bg="gray.700" borderRadius="md">
                      <Spinner size="xl" color="purple.400" />
                    </Flex>
                  }
                />
              </AspectRatio>
              <Link href={scenario.patientImageUrl} isExternal color="blue.400" fontSize="sm" mt={2}>
                View Full Size
              </Link>
            </Box>
          )}
          
          {/* Scene Arrival Video */}
          {scenario?.sceneArrivalVideoUrl && (
            <Box>
              <Heading size="sm" mb={3}>Scene Arrival Video</Heading>
              <AspectRatio ratio={16/9} maxW="800px">
                <video 
                  controls 
                  style={{ borderRadius: '8px' }}
                  preload="metadata"
                >
                  <source src={scenario.sceneArrivalVideoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </AspectRatio>
              <Text fontSize="sm" color="gray.400" mt={2}>
                First-person POV arrival at scene
              </Text>
            </Box>
          )}
          
          {/* Clinical Progression Videos */}
          {(scenario?.deteriorationVideoUrl || scenario?.improvementVideoUrl) && (
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {scenario?.deteriorationVideoUrl && (
                <Box>
                  <Heading size="sm" mb={3}>Patient Deterioration</Heading>
                  <AspectRatio ratio={16/9}>
                    <video 
                      controls 
                      style={{ borderRadius: '8px' }}
                      preload="metadata"
                    >
                      <source src={scenario.deteriorationVideoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </AspectRatio>
                  <Text fontSize="sm" color="gray.400" mt={2}>
                    Without intervention
                  </Text>
                </Box>
              )}
              
              {scenario?.improvementVideoUrl && (
                <Box>
                  <Heading size="sm" mb={3}>Patient Improvement</Heading>
                  <AspectRatio ratio={16/9}>
                    <video 
                      controls 
                      style={{ borderRadius: '8px' }}
                      preload="metadata"
                    >
                      <source src={scenario.improvementVideoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </AspectRatio>
                  <Text fontSize="sm" color="gray.400" mt={2}>
                    With proper treatment
                  </Text>
                </Box>
              )}
            </SimpleGrid>
          )}
          
          {/* Show generation status if still pending */}
          {isGenerating && (
            <Alert status="info" borderRadius="md">
              <AlertIcon as={FaSpinner} animation="spin 1s linear infinite" />
              <Box>
                <AlertTitle>Media Generation In Progress</AlertTitle>
                <AlertDescription>
                  Videos are being generated in the background. This typically takes 1-2 minutes per video.
                  Refresh this page to see updates.
                </AlertDescription>
              </Box>
            </Alert>
          )}
          
          {/* Show error if generation failed */}
          {hasFailed && scenario?.mediaGenerationError && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Media Generation Failed</AlertTitle>
                <AlertDescription>
                  {scenario.mediaGenerationError}
                </AlertDescription>
              </Box>
            </Alert>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

const ScenarioDisplay = ({ 
  scenario, 
  metadata, 
  evaluationForm,
  evaluationMode = false,
  evaluationData = {},
  onEvaluationChange = () => {},
  watermarkText = 'EVALUATION COPY'
}) => {
  // Debug logging
  useEffect(() => {
    console.log('=== METADATA DEBUG ===');
    console.log('metadata prop:', metadata);
    console.log('metadata.levelOfCare:', metadata?.levelOfCare);
    console.log('metadata.mechanismNatureOfInjury:', metadata?.mechanismNatureOfInjury);
    console.log('=== END METADATA DEBUG ===');
  }, [metadata]);

  const [printMode, setPrintMode] = useState('normal'); // 'normal', 'simple-print', 'tablet-view', 'accessible'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  const [orientationMode, setOrientationMode] = useState('auto'); // 'auto', 'landscape', 'portrait'
  const [isHighContrast, setIsHighContrast] = useState(true); // Default to white on black
  const [shouldForcePageBreak, setShouldForcePageBreak] = useState(false);
  const [watermarkConfig, setWatermarkConfig] = useState(null);
  const [showRawUserInput, setShowRawUserInput] = useState(false);
  const [showRawInput, setShowRawInput] = useState(false);
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [currentMedia, setCurrentMedia] = useState(null); // { type: 'image' | 'video', url: string, title: string }

  // Get current user for watermarking
  const user = useUser(api);

  // Helper to check if user has admin role
  const isAdmin = () => {
    if (!user?.roles) return false;
    
    // Check if user has admin or system-admin role
    return user.roles.some(role => {
      // Handle both string roles and role objects with key property
      if (typeof role === 'string') {
        return role === 'admin' || role === 'system-admin';
      } else if (role && typeof role === 'object') {
        return role.key === 'admin' || role.key === 'system-admin' || role === 'admin' || role === 'system-admin';
      }
      return false;
    });
  };
  
  // Fetch watermark configuration
  const [{ data: configData, fetching: fetchingConfig }, getWatermarkConfig] = useGlobalAction(api.getWatermarkConfig);

  useEffect(() => {
    // Fetch watermark config on mount
    if (user?.institution || user?.id) {
      getWatermarkConfig({
        institution: user?.institution,
        userId: user?.id
      });
    }
  }, [user?.institution, user?.id]);

  useEffect(() => {
    // Update watermark config when data arrives
    if (configData) {
      setWatermarkConfig(configData);
    }
  }, [configData]);

  // Evaluation checkbox handlers
  const handleCheckboxToggle = (category, field, value) => {
    if (!evaluationMode) return;
    
    const newData = { ...evaluationData };
    if (!newData[category]) {
      newData[category] = {};
    }
    newData[category][field] = value;
    onEvaluationChange(newData);
  };

  const isChecked = (category, field) => {
    return evaluationData?.[category]?.[field] || false;
  };



  const handlePrintMode = (mode) => {
    console.log('==== TABLET VIEW DEBUG ====');
    console.log('Setting print mode to:', mode);
    console.log('Current body className BEFORE:', document.body.className);
    console.log('isLandscape:', isLandscape);
    
    setPrintMode(mode);
    
    if (mode === 'simple-print') {
      // Simple print renders on screen FIRST
      document.body.className = 'simple-print';
      console.log('Body className set to: simple-print');
      // User can then click Print button to actually print
      // Do NOT auto-trigger window.print()
    } else if (mode === 'tablet-view') {
      const newClassName = `tablet-view ${isLandscape ? 'landscape' : 'portrait'}`;
      console.log('Setting body className to:', newClassName);
      document.body.className = newClassName;
      console.log('Body className AFTER:', document.body.className);
      console.log('Tablet controls element:', document.querySelector('.tablet-controls'));
      console.log('No-print element:', document.querySelector('.no-print'));
      console.log('Scenario header element:', document.querySelector('.scenario-header'));
      // Try to enter fullscreen but don't fail if it doesn't work
      enterFullscreen();
    } else if (mode === 'accessible') {
      // Apply accessible mode - defaults to high contrast (white on black)
      document.body.className = 'accessible high-contrast';
      console.log('Body className set to:', document.body.className);
    } else {
      document.body.className = mode;
      console.log('Body className set to:', mode);
      if (isFullscreen) {
        exitFullscreen();
      }
    }
    console.log('==== END TABLET VIEW DEBUG ====');
  };

  const toggleHighContrast = () => {
    const newHighContrast = !isHighContrast;
    setIsHighContrast(newHighContrast);
    if (printMode === 'accessible') {
      document.body.className = newHighContrast ? 'accessible high-contrast' : 'accessible';
    }
  };
  
  const enterFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen().then(() => setIsFullscreen(true)).catch(err => console.log(err));
    } else if (elem.webkitRequestFullscreen) { /* Safari */
      elem.webkitRequestFullscreen();
      setIsFullscreen(true);
    } else if (elem.msRequestFullscreen) { /* IE11 */
      elem.msRequestFullscreen();
      setIsFullscreen(true);
    }
  };
  
  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(err => console.log(err));
    } else if (document.webkitExitFullscreen) { /* Safari */
      document.webkitExitFullscreen();
      setIsFullscreen(false);
    } else if (document.msExitFullscreen) { /* IE11 */
      document.msExitFullscreen();
      setIsFullscreen(false);
    }
  };

  const setOrientation = (mode) => {
    console.log('==== ORIENTATION CHANGE DEBUG ====');
    console.log('Setting orientation mode to:', mode);
    console.log('Current printMode:', printMode);
    console.log('Current isLandscape:', isLandscape);
    console.log('Window dimensions:', window.innerWidth, 'x', window.innerHeight);
    
    setOrientationMode(mode);
    
    if (mode === 'landscape') {
      console.log('Forcing landscape orientation');
      setIsLandscape(true);
      if (printMode === 'tablet-view') {
        document.body.className = 'tablet-view landscape';
        console.log('Body className set to: tablet-view landscape');
      }
    } else if (mode === 'portrait') {
      console.log('Forcing portrait orientation');
      setIsLandscape(false);
      if (printMode === 'tablet-view') {
        document.body.className = 'tablet-view portrait';
        console.log('Body className set to: tablet-view portrait');
      }
    } else {
      // auto - detect current
      const newIsLandscape = window.innerWidth > window.innerHeight;
      console.log('Auto-detecting orientation. New isLandscape:', newIsLandscape);
      setIsLandscape(newIsLandscape);
      if (printMode === 'tablet-view') {
        const newClassName = `tablet-view ${newIsLandscape ? 'landscape' : 'portrait'}`;
        document.body.className = newClassName;
        console.log('Body className set to:', newClassName);
      }
    }
    console.log('==== END ORIENTATION CHANGE DEBUG ====');
  };

  const checkContentHeight = () => {
    if (printMode !== 'simple-print') return;
    
    // Get the element before the page break
    const pageBreakEl = document.querySelector('.page-break-before-differentials');
    if (!pageBreakEl) return;
    
    // Calculate height of content before the break
    const contentBeforeBreak = pageBreakEl.getBoundingClientRect().top;
    
    // Standard US Letter page height at 96 DPI minus margins (roughly 950px for content area)
    const pageHeight = 950;
    
    // If content fits on one page (with some buffer), force the page break
    // If content spills onto page 2 anyway, don't force it
    if (contentBeforeBreak < pageHeight) {
      setShouldForcePageBreak(true);
    } else {
      setShouldForcePageBreak(false);
    }
  };

  const exitTabletMode = () => {
    setPrintMode('view');
    document.body.className = 'view';
    exitFullscreen(); // Always try to exit, even if we're not sure we're in fullscreen
  };

  const generateFilename = () => {
    return buildScenarioDownloadFilename(
      { ...scenario, id: metadata?.id || scenario?.id },
      "pdf",
      metadata?.title || "clinical-scenario"
    );
  };

  const openMediaModal = (type, url, title) => {
    setCurrentMedia({ type, url, title });
    setMediaModalOpen(true);
  };

  const handleSaveTabletPDF = async (pageMode = 'single') => {
    // For two-page mode, just use native print with the CSS we already have
    if (pageMode === 'two') {
      // Switch to tablet view temporarily
      const originalPrintMode = printMode;
      const originalBodyClass = document.body.className;
      
      setPrintMode('tablet-view');
      document.body.className = 'tablet-view portrait two-page-mode';
      
      // Wait for styles to apply
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Use native print dialog
      window.print();
      
      // Restore after a delay to allow print dialog to capture styles
      setTimeout(() => {
        setPrintMode(originalPrintMode);
        document.body.className = originalBodyClass;
      }, 500);
      
      return;
    }
    
    // Import libraries
    const { jsPDF } = await import('jspdf');
    const html2canvas = await import('html2canvas');
    
    // Switch to tablet view temporarily
    const originalPrintMode = printMode;
    const originalBodyClass = document.body.className;
    
    // Apply tablet-view styles
    setPrintMode('tablet-view');
    document.body.className = 'tablet-view portrait';
    
    // Wait for styles to apply
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Get the main content element
    const element = document.querySelector('.scenario-content-for-pdf') || document.body;
    
    // Create canvas with optimized settings for file size
    const canvas = await html2canvas.default(element, {
      scale: 1.5, // Balanced quality and file size
      useCORS: true,
      logging: false,
      imageTimeout: 0,
      removeContainer: true,
      allowTaint: true,
      windowWidth: 800 // Smaller window
    });
    
    // Use JPEG with higher compression for smaller file size
    const imgData = canvas.toDataURL('image/jpeg', 0.7); // 70% quality JPEG
    const pdf = new jsPDF('p', 'pt', 'letter');
    
    // Get PDF page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // Define margins
    const margin = 20;
    const availableWidth = pageWidth - (2 * margin);
    const availableHeight = pageHeight - (2 * margin);
    
    // Calculate aspect ratios
    const canvasAspect = canvas.width / canvas.height;
    const pageAspect = availableWidth / availableHeight;
    
    // Calculate scaled dimensions to fit content on one page with margins
    let scaledWidth, scaledHeight, xOffset, yOffset;
    
    if (canvasAspect > pageAspect) {
      // Image is wider relative to page - fit to width
      scaledWidth = availableWidth;
      scaledHeight = availableWidth / canvasAspect;
      xOffset = margin;
      yOffset = margin + (availableHeight - scaledHeight) / 2;
    } else {
      // Image is taller relative to page - fit to height
      scaledHeight = availableHeight;
      scaledWidth = availableHeight * canvasAspect;
      xOffset = margin + (availableWidth - scaledWidth) / 2;
      yOffset = margin;
    }
    
    // Add image to PDF with FAST compression
    pdf.addImage(imgData, 'JPEG', xOffset, yOffset, scaledWidth, scaledHeight, undefined, 'FAST');
    
    // Restore original mode
    setPrintMode(originalPrintMode);
    document.body.className = originalBodyClass;
    
    // Save PDF
    pdf.save(generateFilename());
  };

  const handleSaveSimplePDF = async () => {
    // When user clicks Print in simple mode, THEN trigger print
    window.print();
  };

  const handlePrint = () => {
    // Always use native browser print - the CSS will handle formatting correctly
    window.print();
  };

  useEffect(() => {
    console.log('=== SCENARIO DISPLAY COMPONENT DEBUG ===');
    console.log('Received scenario prop:', scenario);
    console.log('Has patientInfo?', !!scenario?.patientInfo);
    console.log('Has sceneSetup?', !!scenario?.sceneSetup);
    console.log('Has interventions?', !!scenario?.interventions);
    console.log('Interventions (camelCase):', scenario?.interventions);
    console.log('Interventions (PascalCase):', scenario?.Interventions);
    console.log('Has learningObjectives?', !!scenario?.learningObjectives);
    console.log('Learning objectives (camelCase):', scenario?.learningObjectives);
    console.log('Learning objectives (PascalCase):', scenario?.EducationalComponents?.LearningObjectives);
    console.log('Has criticalActions?', !!scenario?.criticalActions);
    console.log('Critical actions (camelCase):', scenario?.criticalActions);
    console.log('Critical actions (PascalCase):', scenario?.EducationalComponents?.CriticalActions);
    console.log('Has commonPitfalls?', !!scenario?.commonPitfalls);
    console.log('Has differentialDiagnosis?', !!scenario?.differentialDiagnosis);
    console.log('Has secondaryAssessment (camelCase)?', !!scenario?.secondaryAssessment);
    console.log('Has SecondaryAssessment (PascalCase)?', !!scenario?.AssessmentFindings?.SecondaryAssessment);
    console.log('secondaryAssessment data:', scenario?.secondaryAssessment);
    console.log('SecondaryAssessment data:', scenario?.AssessmentFindings?.SecondaryAssessment);
    console.log('=== END COMPONENT DEBUG ===');
  }, [scenario]);

  useEffect(() => {
    // Listen for fullscreen changes
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement);
      setIsFullscreen(isCurrentlyFullscreen);
      
      // Don't auto-exit tablet mode when fullscreen exits
      // User can manually exit with the Exit button
    };
    
    // Listen for orientation changes
    const handleOrientationChange = () => {
      if (orientationMode === 'auto') {
        setIsLandscape(window.innerWidth > window.innerHeight);
      }
      // If locked to landscape or portrait, don't update
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Cleanup on unmount
    return () => {
      document.body.className = '';
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (isFullscreen) {
        exitFullscreen();
      }
    };
  }, [isFullscreen, printMode, orientationMode]);

  useEffect(() => {
    console.log('useEffect triggered - printMode:', printMode, 'isLandscape:', isLandscape, 'isHighContrast:', isHighContrast);
    // Sync body class with print mode and orientation
    if (printMode === 'tablet-view') {
      const newClassName = `tablet-view ${isLandscape ? 'landscape' : 'portrait'}`;
      console.log('useEffect setting className to:', newClassName);
      document.body.className = newClassName;
    } else if (printMode === 'accessible') {
      document.body.className = isHighContrast ? 'accessible high-contrast' : 'accessible';
    } else {
      document.body.className = printMode;
    }
  }, [printMode, isLandscape, isHighContrast]);

  useEffect(() => {
    if (printMode === 'simple-print') {
      // Wait for layout to settle
      setTimeout(checkContentHeight, 100);
    }
  }, [printMode]);

  const renderOvertWatermark = () => {
    if (!watermarkConfig || !watermarkConfig.watermarkingEnabled) return null;

    const { overtWatermarkText, overtWatermarkPosition, overtWatermarkColor, overtWatermarkOpacity } = watermarkConfig;
    const institutionName = watermarkConfig.institution || user?.institution || 'Educational Use Only';
    const timestamp = new Date().toISOString();
    const displayText = overtWatermarkText || `${institutionName} • ${new Date().getFullYear()} • Educational Use Only`;
    const color = overtWatermarkColor || '#000000';
    const opacity = overtWatermarkOpacity || 0.15;

    const baseStyle = {
      pointerEvents: 'none',
      userSelect: 'none',
      color: color,
      opacity: opacity,
      fontWeight: 'bold',
      zIndex: 9998,
    };

    switch (overtWatermarkPosition) {
      case 'diagonal':
        return (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            overflow="hidden"
            {...baseStyle}
          >
            {[...Array(20)].map((_, i) => (
              <Text
                key={i}
                position="absolute"
                fontSize="48px"
                transform={`rotate(-45deg) translate(${i * 200}px, ${i * 150}px)`}
                whiteSpace="nowrap"
              >
                {watermarkText}
              </Text>
            ))}
          </Box>
        );
      
      case 'top':
        return (
          <Box
            position="absolute"
            top="20px"
            left="50%"
            transform="translateX(-50%)"
            {...baseStyle}
          >
            <Text fontSize="32px" textAlign="center">
              {watermarkText}
            </Text>
          </Box>
        );
      
      case 'bottom':
        return (
          <Box
            position="absolute"
            bottom="20px"
            left="50%"
            transform="translateX(-50%)"
            {...baseStyle}
          >
            <Text fontSize="32px" textAlign="center">
              {watermarkText}
            </Text>
          </Box>
        );
      
      case 'corners':
        return (
          <>
            <Box position="absolute" top="10px" left="10px" {...baseStyle}>
              <Text fontSize="24px">{watermarkText}</Text>
            </Box>
            <Box position="absolute" top="10px" right="10px" {...baseStyle}>
              <Text fontSize="24px">{watermarkText}</Text>
            </Box>
            <Box position="absolute" bottom="10px" left="10px" {...baseStyle}>
              <Text fontSize="24px">{watermarkText}</Text>
            </Box>
            <Box position="absolute" bottom="10px" right="10px" {...baseStyle}>
              <Text fontSize="24px">{watermarkText}</Text>
            </Box>
          </>
        );
      
      default:
        return null;
    }
  };

  const renderSubtleWatermark = () => {
    if (!watermarkConfig || !watermarkConfig.watermarkingEnabled || !watermarkConfig.subtleWatermarkEnabled) return null;

    const watermarkData = {
      scenario: scenario?.id,
      viewer: user?.id,
      institution: watermarkConfig.institution || user?.institution,
      timestamp: new Date().toISOString(),
      pattern: watermarkConfig.subtleWatermarkPattern || 'default-pattern',
    };

    return (
      <Box
        position="absolute"
        opacity={0.001}
        fontSize="1px"
        top={0}
        left={0}
        pointerEvents="none"
        userSelect="none"
        data-watermark-scenario={scenario?.id}
        data-watermark-viewer={user?.id}
        data-watermark-institution={user?.institution}
        data-watermark-timestamp={watermarkData.timestamp}
        data-watermark-pattern={watermarkConfig.subtleWatermarkPattern}
        className="subtle-watermark"
      >
        {JSON.stringify(watermarkData)}
      </Box>
    );
  };

  const generateTextOnlyPDF = () => {
    let text = '';
    
    // Helper to add section
    const addSection = (title) => {
      text += `\n\n${title.toUpperCase()}\n`;
      text += '-'.repeat(title.length) + '\n';
    };
    
    // Helper to check if value exists and is not N/A or Unknown
    const isValid = (value) => {
      if (!value) return false;
      if (typeof value === 'string') {
        const normalized = value.toLowerCase().trim();
        return normalized !== 'n/a' && normalized !== 'unknown' && normalized !== '';
      }
      return true;
    };
    
    // Helper to format vital
    const formatVitalText = (vital) => {
      if (!vital) return '';
      if (typeof vital === 'object') {
        return vital.value ? `${vital.value} ${vital.unit || ''}`.trim() : '';
      }
      return vital.toString();
    };
    
    // Header Block
    text += 'CLINICAL SCENARIO\n';
    text += '='.repeat(80) + '\n';
    if (metadata?.levelOfCare && isValid(metadata.levelOfCare)) {
      text += `Training Level: ${metadata.levelOfCare.replace(/_/g, ' ')}\n`;
    }
    if (patientInfo.age && isValid(patientInfo.age)) {
      text += `Patient Age: ${patientInfo.age}\n`;
    }
    if (scenario.chiefComplaint && isValid(scenario.chiefComplaint)) {
      text += `Chief Complaint: ${scenario.chiefComplaint}\n`;
    }
    if (metadata?.mechanismNatureOfInjury && isValid(metadata.mechanismNatureOfInjury)) {
      text += `Mechanism/Nature of Injury: ${metadata.mechanismNatureOfInjury}\n`;
    }
    text += '='.repeat(80) + '\n';
    text += '\n\n';
    
    // Metadata
    if (metadata?.levelOfCare || scenario.difficulty || metadata?.mechanismNatureOfInjury) {
      addSection('Scenario Metadata');
      if (metadata?.levelOfCare) {
        text += `  Training Level: ${metadata.levelOfCare.replace(/_/g, ' ')}\n`;
      }
      if (scenario.difficulty) {
        text += `  Difficulty: ${scenario.difficulty} out of 5\n`;
      }
      if (metadata?.mechanismNatureOfInjury) {
        text += `  Mechanism/Nature of Injury: ${metadata.mechanismNatureOfInjury}\n`;
      }
    }
    
    // Background and Dispatch
    addSection('Background and Dispatch Information');
    if (sceneSetup.timeOfDay && isValid(sceneSetup.timeOfDay)) {
      text += `  Time of Day: ${sceneSetup.timeOfDay}\n`;
    }
    if (sceneSetup.location && isValid(sceneSetup.location)) {
      text += `  Location: ${sceneSetup.location}\n`;
    }
    if (sceneSetup.weather && isValid(sceneSetup.weather)) {
      text += `  Weather: ${sceneSetup.weather}\n`;
    }
    if (sceneSetup.mechanism && isValid(sceneSetup.mechanism)) {
      text += `  Mechanism/Nature: ${sceneSetup.mechanism}\n`;
    }
    if (sceneSetup.dispatchInfo && isValid(sceneSetup.dispatchInfo)) {
      text += `  Dispatch Information: ${sceneSetup.dispatchInfo}\n`;
    }
    if ((sceneSetup.safetyConsiderations || sceneSetup.safetyIssues) && isValid(sceneSetup.safetyConsiderations || sceneSetup.safetyIssues)) {
      text += `  SAFETY ALERT: ${sceneSetup.safetyConsiderations || sceneSetup.safetyIssues}\n`;
    }
    if (sceneSetup.bystanders && isValid(sceneSetup.bystanders)) {
      text += `  Bystanders/Scene: ${sceneSetup.bystanders}\n`;
    }
    
    // Patient Information
    addSection('Patient Information');
    if (patientInfo.name && isValid(patientInfo.name)) {
      text += `  Name: ${patientInfo.name}\n`;
    }
    if (patientInfo.age && isValid(patientInfo.age)) {
      text += `  Age: ${patientInfo.age}\n`;
    }
    if (patientInfo.sex && isValid(patientInfo.sex)) {
      text += `  Sex: ${patientInfo.sex}\n`;
    }
    if (patientInfo.weight && isValid(patientInfo.weight)) {
      text += `  Weight: ${patientInfo.weight}\n`;
    }
    if (patientInfo.physicalAppearance && isValid(patientInfo.physicalAppearance)) {
      text += `  Physical Appearance: ${patientInfo.physicalAppearance}\n`;
    }
    
    // Chief Complaint and History
    addSection('Chief Complaint and History');
    if (scenario.chiefComplaint && isValid(scenario.chiefComplaint)) {
      text += `  Chief Complaint: ${scenario.chiefComplaint}\n`;
    }
    if ((history.hpi || scenario.enhancedHpi) && (isValid(history.hpi) || isValid(scenario.enhancedHpi))) {
      text += `  History of Present Illness: ${scenario.enhancedHpi || history.hpi}\n`;
    }
    if (history.allergies && isValid(history.allergies)) {
      text += `  ALLERGIES: ${history.allergies}\n`;
    }
    if (history.medications && isValid(history.medications)) {
      text += `  Medications: ${history.medications}\n`;
    }
    if (history.pmhx && isValid(history.pmhx)) {
      text += `  Past Medical History: ${history.pmhx}\n`;
    }
    if (history.lastMeal && isValid(history.lastMeal)) {
      text += `  Last Oral Intake: ${history.lastMeal}\n`;
    }
    if (history.events && isValid(history.events)) {
      text += `  Events Leading to Incident: ${history.events}\n`;
    }
    
    // Primary Assessment
    addSection('Primary Assessment');
    if (primaryAssessment.mentalStatus && isValid(primaryAssessment.mentalStatus)) {
      text += `  Level of Consciousness: ${primaryAssessment.mentalStatus}\n`;
    }
    if (primaryAssessment.airway && isValid(primaryAssessment.airway)) {
      text += `  Airway: ${primaryAssessment.airway}\n`;
    }
    if (primaryAssessment.breathing && isValid(primaryAssessment.breathing)) {
      text += `  Breathing: ${primaryAssessment.breathing}\n`;
    }
    if (primaryAssessment.circulation && isValid(primaryAssessment.circulation)) {
      text += `  Circulation: ${primaryAssessment.circulation}\n`;
    }
    if (primaryAssessment.skinColor || primaryAssessment.skinTemp || primaryAssessment.skinMoisture) {
      const skinParts = [
        primaryAssessment.skinColor && isValid(primaryAssessment.skinColor) ? primaryAssessment.skinColor : null,
        primaryAssessment.skinTemp && isValid(primaryAssessment.skinTemp) ? primaryAssessment.skinTemp : null,
        primaryAssessment.skinMoisture && isValid(primaryAssessment.skinMoisture) ? primaryAssessment.skinMoisture : null
      ].filter(Boolean);
      if (skinParts.length > 0) {
        text += `  Skin: ${skinParts.join(', ')}\n`;
      }
    }
    
    // Initial Vital Signs
    if (vitals.initial) {
      text += `\n  Initial Vital Signs:\n`;
      if (vitals.initial.hr && formatVitalText(vitals.initial.hr)) {
        text += `    - Heart Rate: ${formatVitalText(vitals.initial.hr)}\n`;
      }
      if (vitals.initial.bp && formatVitalText(vitals.initial.bp)) {
        text += `    - Blood Pressure: ${formatVitalText(vitals.initial.bp)}\n`;
      }
      if (vitals.initial.rr && formatVitalText(vitals.initial.rr)) {
        text += `    - Respiratory Rate: ${formatVitalText(vitals.initial.rr)}\n`;
      }
      if (vitals.initial.spo2 && formatVitalText(vitals.initial.spo2)) {
        text += `    - SpO2: ${formatVitalText(vitals.initial.spo2)}\n`;
      }
      if (vitals.initial.temp && formatVitalText(vitals.initial.temp)) {
        text += `    - Temperature: ${formatVitalText(vitals.initial.temp)}\n`;
      }
      if (vitals.initial.bloodGlucose && formatVitalText(vitals.initial.bloodGlucose)) {
        text += `    - Blood Glucose: ${formatVitalText(vitals.initial.bloodGlucose)}\n`;
      }
      if (vitals.initial.rhythm && formatVitalText(vitals.initial.rhythm)) {
        text += `    - Cardiac Rhythm: ${formatVitalText(vitals.initial.rhythm)}\n`;
      }
      if (vitals.initial.etco2 && formatVitalText(vitals.initial.etco2)) {
        text += `    - End-Tidal CO2: ${formatVitalText(vitals.initial.etco2)}\n`;
      }
      if (vitals.initial.pupils && isValid(vitals.initial.pupils)) {
        text += `    - Pupils: ${vitals.initial.pupils}\n`;
      }
      if (vitals.initial.gcs && isValid(vitals.initial.gcs)) {
        text += `    - Glasgow Coma Scale: ${vitals.initial.gcs}\n`;
      }
    }
    
    // Secondary Assessment
    if (secondaryAssessment && Object.keys(secondaryAssessment).some(key => isValid(secondaryAssessment[key]))) {
      addSection('Secondary Assessment (Physical Exam)');
      if (secondaryAssessment.heent && isValid(secondaryAssessment.heent)) {
        text += `  HEENT: ${secondaryAssessment.heent}\n`;
      }
      if (secondaryAssessment.neck && isValid(secondaryAssessment.neck)) {
        text += `  Neck: ${secondaryAssessment.neck}\n`;
      }
      if (secondaryAssessment.chest && isValid(secondaryAssessment.chest)) {
        text += `  Chest: ${secondaryAssessment.chest}\n`;
      }
      if (secondaryAssessment.respiratory && isValid(secondaryAssessment.respiratory)) {
        text += `  Respiratory: ${secondaryAssessment.respiratory}\n`;
      }
      if (secondaryAssessment.cardiovascular && isValid(secondaryAssessment.cardiovascular)) {
        text += `  Cardiovascular: ${secondaryAssessment.cardiovascular}\n`;
      }
      if (secondaryAssessment.abdomen && isValid(secondaryAssessment.abdomen)) {
        text += `  Abdomen: ${secondaryAssessment.abdomen}\n`;
      }
      if (secondaryAssessment.pelvis && isValid(secondaryAssessment.pelvis)) {
        text += `  Pelvis: ${secondaryAssessment.pelvis}\n`;
      }
      if (secondaryAssessment.extremities && isValid(secondaryAssessment.extremities)) {
        text += `  Extremities: ${secondaryAssessment.extremities}\n`;
      }
      if (secondaryAssessment.back && isValid(secondaryAssessment.back)) {
        text += `  Back: ${secondaryAssessment.back}\n`;
      }
      if (secondaryAssessment.neuro && isValid(secondaryAssessment.neuro)) {
        text += `  Neurological: ${secondaryAssessment.neuro}\n`;
      }
      if (secondaryAssessment.skin && isValid(secondaryAssessment.skin)) {
        text += `  Skin: ${secondaryAssessment.skin}\n`;
      }
    }
    
    // Trending Vital Signs
    if (vitals.trending && vitals.trending.length > 0) {
      addSection('Trending Vital Signs');
      vitals.trending.forEach((vitalSet, index) => {
        const time = vitalSet.time && isValid(vitalSet.time) ? vitalSet.time : `Time ${index + 1}`;
        text += `  ${time}:\n`;
        if (vitalSet.hr && formatVitalText(vitalSet.hr)) {
          text += `    - HR: ${formatVitalText(vitalSet.hr)}\n`;
        }
        if (vitalSet.bp && formatVitalText(vitalSet.bp)) {
          text += `    - BP: ${formatVitalText(vitalSet.bp)}\n`;
        }
        if (vitalSet.rr && formatVitalText(vitalSet.rr)) {
          text += `    - RR: ${formatVitalText(vitalSet.rr)}\n`;
        }
        if (vitalSet.spo2 && formatVitalText(vitalSet.spo2)) {
          text += `    - SpO2: ${formatVitalText(vitalSet.spo2)}\n`;
        }
        if (vitalSet.temp && formatVitalText(vitalSet.temp)) {
          text += `    - Temp: ${formatVitalText(vitalSet.temp)}\n`;
        }
        if (vitalSet.bloodGlucose && formatVitalText(vitalSet.bloodGlucose)) {
          text += `    - BGL: ${formatVitalText(vitalSet.bloodGlucose)}\n`;
        }
        if (vitalSet.etco2 && formatVitalText(vitalSet.etco2)) {
          text += `    - EtCO2: ${formatVitalText(vitalSet.etco2)}\n`;
        }
        if (vitalSet.gcs && isValid(vitalSet.gcs)) {
          text += `    - GCS: ${vitalSet.gcs}\n`;
        }
      });
    }
    
    // Interventions
    if (interventions && (interventions.LifeSaving?.length > 0 || interventions.Secondary?.length > 0 || interventions.Optional?.length > 0)) {
      addSection('Interventions');
      
      if (interventions.LifeSaving && interventions.LifeSaving.length > 0) {
        text += `\n  Critical/Life-Saving Interventions:\n`;
        interventions.LifeSaving.forEach(intervention => {
          text += `    - ${intervention}\n`;
        });
      }
      
      if (interventions.Secondary && interventions.Secondary.length > 0) {
        text += `\n  Secondary Interventions:\n`;
        interventions.Secondary.forEach(intervention => {
          text += `    - ${intervention}\n`;
        });
      }
      
      if (interventions.Optional && interventions.Optional.length > 0) {
        text += `\n  Optional/Conditional Interventions:\n`;
        interventions.Optional.forEach(intervention => {
          text += `    - ${intervention}\n`;
        });
      }
    }
    
    // Critical Actions
    if (learning.criticalActions && learning.criticalActions.length > 0) {
      addSection('Critical Actions');
      learning.criticalActions.forEach(action => {
        text += `  - ${action}\n`;
      });
    }
    
    // Common Pitfalls
    if (learning.commonPitfalls && learning.commonPitfalls.length > 0) {
      addSection('Common Pitfalls');
      learning.commonPitfalls.forEach(pitfall => {
        text += `  - ${pitfall}\n`;
      });
    }
    
    // Differential Diagnosis
    if (learning.differentialDiagnosis && learning.differentialDiagnosis.length > 0) {
      addSection('Differential Diagnosis');
      learning.differentialDiagnosis.forEach(diagnosis => {
        text += `  - ${diagnosis}\n`;
      });
    }
    
    // Expected Outcome
    if (outcome.finalCondition || outcome.disposition || outcome.continuingCareNeeds) {
      addSection('Expected Outcome');
      if (outcome.finalCondition && isValid(outcome.finalCondition)) {
        text += `  Final Condition: ${outcome.finalCondition}\n`;
      }
      if (outcome.disposition && isValid(outcome.disposition)) {
        text += `  Disposition: ${outcome.disposition}\n`;
      }
      if (outcome.continuingCareNeeds && isValid(outcome.continuingCareNeeds)) {
        text += `  Continuing Care Needs: ${outcome.continuingCareNeeds}\n`;
      }
    }
    
    return text;
  };


  if (!scenario) {
    return (
      <Box p={4}>
        <Text>No scenario data available</Text>
      </Box>
    );
  }

  // Color mode values for dark theme support
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardTextColor = useColorModeValue('gray.900', 'white');
  const boxBg = useColorModeValue('gray.50', 'gray.700');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const textColor = useColorModeValue('gray.800', 'gray.200');
  const boldTextColor = useColorModeValue('gray.700', 'gray.300');
  const alertBgInfo = useColorModeValue('blue.50', 'blue.900');
  const alertBgWarning = useColorModeValue('orange.50', 'orange.900');
  const alertBgError = useColorModeValue('red.50', 'red.900');
  const statsBoxBg = useColorModeValue('white', 'gray.700');
  const interventionCardBg = useColorModeValue('blue.50', 'blue.900');
  const trendingCardBg = useColorModeValue('gray.50', 'gray.700');

  const patientInfo = {
    name: scenario.PatientDemographics?.FullName || scenario.patientInfo?.name,
    age: scenario.PatientDemographics?.Age || scenario.patientInfo?.age,
    weight: scenario.PatientDemographics?.Weight || scenario.patientInfo?.weight,
    sex: scenario.PatientDemographics?.Sex || scenario.patientInfo?.sex,
    ethnicity: scenario.PatientDemographics?.Ethnicity || scenario.patientInfo?.ethnicity,
    physicalAppearance: scenario.PatientDemographics?.PhysicalAppearance || scenario.patientInfo?.appearance || scenario.patientInfo?.physicalAppearance,
    moulage: scenario.PatientDemographics?.Moulage || scenario.patientInfo?.moulage || scenario.sceneSetup?.moulage || scenario.SceneSetup?.Moulage
  };

  const sceneSetup = {
    location: scenario.SceneSetup?.Location || scenario.sceneSetup?.location,
    timeOfDay: scenario.SceneSetup?.TimeOfDay || scenario.sceneSetup?.timeOfDay,
    weather: scenario.SceneSetup?.Weather || scenario.sceneSetup?.weather,
    safetyIssues: scenario.SceneSetup?.SafetyIssues || scenario.sceneSetup?.safetyIssues,
    safetyConsiderations: scenario.SceneSetup?.SafetyIssues || scenario.sceneSetup?.safetyConsiderations,
    dispatchInfo: scenario.SceneSetup?.DispatchInformation || scenario.sceneSetup?.dispatchInfo,
    bystanders: scenario.SceneSetup?.Bystanders || scenario.sceneSetup?.bystanders,
    mechanism: scenario.sceneSetup?.mechanism,
    priorToArrivalCare: scenario.SceneSetup?.PriorToArrivalCare || scenario.sceneSetup?.priorToArrivalCare || 'None',
    transportContext: scenario.SceneSetup?.TransportContext || scenario.sceneSetup?.transportContext,
    flightOperations: scenario.SceneSetup?.FlightOperations || scenario.sceneSetup?.flightOperations
  };

  const primaryAssessment = {
    mentalStatus: scenario.AssessmentFindings?.PrimaryAssessment?.MentalStatus || scenario.VitalSigns?.Initial?.GCS || scenario.primaryAssessment?.mentalStatus,
    airway: scenario.AssessmentFindings?.PrimaryAssessment?.Airway || scenario.primaryAssessment?.airway,
    breathing: scenario.AssessmentFindings?.PrimaryAssessment?.Breathing || scenario.primaryAssessment?.breathing,
    circulation: scenario.AssessmentFindings?.PrimaryAssessment?.Circulation || scenario.primaryAssessment?.circulation,
    skinColor: scenario.AssessmentFindings?.PrimaryAssessment?.Skin?.split(',')[0] || scenario.primaryAssessment?.skinColor,
    skinTemp: scenario.AssessmentFindings?.PrimaryAssessment?.Skin?.includes('cool') ? 'Cool' : scenario.primaryAssessment?.skinTemp,
    skinMoisture: scenario.AssessmentFindings?.PrimaryAssessment?.Skin?.includes('clammy') ? 'Clammy' : scenario.primaryAssessment?.skinMoisture
  };

  const secondaryAssessment = {
    heent: scenario.AssessmentFindings?.SecondaryAssessment?.HEENT || scenario.secondaryAssessment?.heent || scenario.secondaryAssessment?.HEENT,
    neck: scenario.AssessmentFindings?.SecondaryAssessment?.NeckBack || scenario.secondaryAssessment?.neck || scenario.secondaryAssessment?.NeckBack,
    chest: scenario.AssessmentFindings?.SecondaryAssessment?.Chest || scenario.secondaryAssessment?.chest,
    respiratory: scenario.AssessmentFindings?.SecondaryAssessment?.Respiratory || scenario.secondaryAssessment?.respiratory,
    cardiovascular: scenario.AssessmentFindings?.SecondaryAssessment?.Cardiovascular || scenario.secondaryAssessment?.cardiovascular,
    abdomen: scenario.AssessmentFindings?.SecondaryAssessment?.GI || scenario.secondaryAssessment?.abdomen || scenario.secondaryAssessment?.GI,
    pelvis: scenario.AssessmentFindings?.SecondaryAssessment?.Pelvis || scenario.secondaryAssessment?.pelvis,
    extremities: scenario.AssessmentFindings?.SecondaryAssessment?.Musculoskeletal || scenario.secondaryAssessment?.extremities || scenario.secondaryAssessment?.Musculoskeletal,
    back: scenario.AssessmentFindings?.SecondaryAssessment?.Back || scenario.secondaryAssessment?.back,
    neuro: scenario.AssessmentFindings?.SecondaryAssessment?.NervousSystem || scenario.secondaryAssessment?.neuro || scenario.secondaryAssessment?.NervousSystem,
    skin: scenario.AssessmentFindings?.SecondaryAssessment?.Integumentary || scenario.secondaryAssessment?.skin || scenario.secondaryAssessment?.Integumentary
  };

  const vitals = {
    initial: scenario.VitalSigns?.Initial || scenario.vitalSigns?.initial,
    trending: scenario.VitalSigns?.Trending || scenario.vitalSigns?.trending
  };

  // Convert vital sign format if needed
  if (vitals.initial) {
    vitals.initial = {
      hr: vitals.initial.HR || vitals.initial.hr,
      bp: vitals.initial.BP || vitals.initial.bp,
      rr: vitals.initial.RR || vitals.initial.rr,
      spo2: vitals.initial.SpO2 || vitals.initial.spo2,
      etco2: vitals.initial.ETCO2 || vitals.initial.etco2,
      temp: vitals.initial.Temp || vitals.initial.temp,
      bloodGlucose: vitals.initial.BGL || vitals.initial.bloodGlucose,
      rhythm: vitals.initial.Rhythm || vitals.initial.rhythm,
      '12Lead': vitals.initial['12Lead'] || vitals.initial['12-Lead'] || vitals.initial.TwelveLeadECG || vitals.initial.twelveLeadECG,
      pupils: vitals.initial.Pupils || vitals.initial.pupils,
      gcs: vitals.initial.GCS || vitals.initial.gcs
    };
  }

  if (vitals.trending && vitals.trending.length > 0) {
    vitals.trending = vitals.trending.map(v => ({
      time: v.Time || v.time,
      hr: v.HR || v.hr,
      bp: v.BP || v.bp,
      rr: v.RR || v.rr,
      spo2: v.SpO2 || v.spo2,
      etco2: v.ETCO2 || v.etco2,
      temp: v.Temp || v.temp,
      bloodGlucose: v.BGL || v.bloodGlucose,
      gcs: v.GCS || v.gcs
    }));
  }

  const normalizeChecklistText = (value) => {
    if (value == null) return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "object") {
      return String(
        value.label ||
        value.name ||
        value.action ||
        value.title ||
        value.text ||
        value.value ||
        value.description ||
        ""
      ).trim();
    }
    return String(value).trim();
  };

  const makeChecklistKey = (prefix, value, index) => {
    const normalized = normalizeChecklistText(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return `${prefix}_${normalized || index}`;
  };

  // Handle both new categorized format and legacy array format
  let interventions = scenario.Interventions || scenario.interventions;
  if (Array.isArray(interventions)) {
    // Legacy format - convert to categorized with defensive copy
    interventions = {
      LifeSaving: [],
      Secondary: Array.from(interventions.map((i) => normalizeChecklistText(i)).filter(Boolean)),
      Optional: []
    };
  } else if (!interventions || typeof interventions !== 'object') {
    interventions = { LifeSaving: [], Secondary: [], Optional: [] };
  } else {
    const groupedInterventions = {
      LifeSaving: interventions.LifeSaving || interventions.lifeSaving || [],
      Secondary: interventions.Secondary || interventions.secondary || [],
      Optional: interventions.Optional || interventions.optional || []
    };

    // Support flattened intervention lists found in some scenario outputs.
    if (groupedInterventions.LifeSaving.length === 0 && scenario.criticalInterventions) {
      groupedInterventions.LifeSaving = scenario.criticalInterventions;
    }
    if (groupedInterventions.Secondary.length === 0 && scenario.secondaryInterventions) {
      groupedInterventions.Secondary = scenario.secondaryInterventions;
    }
    if (groupedInterventions.Optional.length === 0 && scenario.optionalInterventions) {
      groupedInterventions.Optional = scenario.optionalInterventions;
    }

    // Create mutable copies of all intervention arrays to prevent read-only errors
    interventions = {
      LifeSaving: Array.from(groupedInterventions.LifeSaving || []).map((i) => normalizeChecklistText(i)).filter(Boolean),
      Secondary: Array.from(groupedInterventions.Secondary || []).map((i) => normalizeChecklistText(i)).filter(Boolean),
      Optional: Array.from(groupedInterventions.Optional || []).map((i) => normalizeChecklistText(i)).filter(Boolean)
    };
  }

  const deteriorationVitals = scenario.DeteriorationVitals || scenario.deteriorationVitals;
  const failedManagement = scenario.FailedManagementDescription || scenario.failedManagementDescription;

  const history = {
    hpi: scenario.History?.HPI ? `${scenario.History.HPI.Onset || ''} ${scenario.History.HPI.Provocation || ''} ${scenario.History.HPI.Quality || ''} ${scenario.History.HPI.Severity || ''} ${scenario.History.HPI.Time || ''}`.trim() : scenario.history?.hpi,
    events: scenario.History?.Events || scenario.history?.events,
    pmhx: scenario.History?.PMHx || scenario.history?.pmhx,
    medications: scenario.History?.Medications || scenario.history?.medications,
    allergies: scenario.History?.Allergies || scenario.history?.allergies,
    lastMeal: scenario.History?.LastMeal || scenario.history?.lastMeal
  };

  const learning = {
    learningObjectives: Array.from(scenario.EducationalComponents?.LearningObjectives || scenario.learningObjectives || []),
    criticalActions: Array.from(
      scenario.EducationalComponents?.CriticalActions ||
      scenario.criticalActions ||
      scenario.CriticalActions ||
      scenario.mandatoryActions ||
      []
    ).map((item) => normalizeChecklistText(item)).filter(Boolean),
    commonPitfalls: Array.from(
      scenario.EducationalComponents?.CommonPitfalls ||
      scenario.commonPitfalls ||
      scenario.CommonPitfalls ||
      scenario.pitfalls ||
      []
    ).map((item) => normalizeChecklistText(item)).filter(Boolean),
    differentialDiagnosis: Array.from(scenario.EducationalComponents?.DifferentialDiagnosis || scenario.differentialDiagnosis || [])
  };

  // Proctor Notes
  const proctorNotes = {
    timingGuidance: scenario.proctorNotes?.timingGuidance || scenario.ProctorNotes?.TimingGuidance,
    progressionCues: scenario.proctorNotes?.progressionCues || scenario.ProctorNotes?.ProgressionCues,
    criticalTimings: scenario.proctorNotes?.criticalTimings || scenario.ProctorNotes?.CriticalTimings
  };

  const hasProctorNotes = proctorNotes.timingGuidance || proctorNotes.progressionCues || proctorNotes.criticalTimings;

  // Cardiac Arrest Sequence
  const cardiacArrest = {
    applicable: scenario.cardiacArrestSequence?.applicable || scenario.CardiacArrestSequence?.Applicable,
    initialRhythm: scenario.cardiacArrestSequence?.initialRhythm || scenario.CardiacArrestSequence?.InitialRhythm,
    sequence: Array.from(scenario.cardiacArrestSequence?.sequence || scenario.CardiacArrestSequence?.Sequence || []),
    targetOutcome: scenario.cardiacArrestSequence?.targetOutcome || scenario.CardiacArrestSequence?.TargetOutcome,
    postResuscitationCare: scenario.cardiacArrestSequence?.postResuscitationCare || scenario.CardiacArrestSequence?.PostResuscitationCare
  };

  const hasCardiacArrest = cardiacArrest.applicable === true || cardiacArrest.applicable === 'true';

  // Transport Context (FP-C/CCP-C)
  const transportContext = scenario.sceneSetup?.transportContext || scenario.SceneSetup?.TransportContext;

  // Moulage
  const moulage = scenario.sceneSetup?.moulage || scenario.SceneSetup?.Moulage;

  // Definitive Care
  const definitiveCare = {
    labTests: scenario.definitiveCare?.labTests || scenario.DefinitiveCare?.LabTests || [],
    imagingStudies: scenario.definitiveCare?.imagingStudies || scenario.DefinitiveCare?.ImagingStudies || [],
    diagnosticProcedures: scenario.definitiveCare?.diagnosticProcedures || scenario.DefinitiveCare?.DiagnosticProcedures || [],
    specialistConsultations: scenario.definitiveCare?.specialistConsultations || scenario.DefinitiveCare?.SpecialistConsultations || [],
    definitiveInterventions: scenario.definitiveCare?.definitiveInterventions || scenario.DefinitiveCare?.DefinitiveInterventions || []
  };

  const hasDefinitiveCare = definitiveCare.labTests.length > 0 || 
                           definitiveCare.imagingStudies.length > 0 || 
                           definitiveCare.diagnosticProcedures.length > 0 || 
                           definitiveCare.specialistConsultations.length > 0 ||
                           definitiveCare.definitiveInterventions.length > 0;

  const outcome = {
    finalCondition: scenario.ExpectedOutcome?.FinalCondition || scenario.expectedOutcome?.finalCondition,
    disposition: scenario.ExpectedOutcome?.Disposition || scenario.expectedOutcome?.disposition,
    continuingCareNeeds: scenario.ExpectedOutcome?.ContinuingCareNeeds || scenario.expectedOutcome?.continuingCareNeeds
  };

  // Parse BP for status checking
  const getBPStatus = (bp) => {
    if (!bp) return 'normal';
    const formatted = formatVital(bp);
    if (!formatted) return 'normal';
    const systolic = parseInt(formatted.split('/')[0]);
    return getVitalStatus('bp_systolic', systolic);
  };

  const renderAccessibleContent = () => {
    const bgColor = isHighContrast ? '#000000' : '#FFFFFF';
    const textColor = isHighContrast ? '#FFFFFF' : '#000000';
    
    // Helper to sanitize text and remove markdown/formatting
    const sanitizeText = (text) => {
      if (!text) return '';
      return String(text)
        .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold markdown, keep text
        .replace(/\*(.+?)\*/g, '$1')   // Remove italic markdown, keep text
        .replace(/^[-•–—]\s+/gm, '') // Remove list markers (dash, bullet, en-dash, em-dash)
        .replace(/^\s*[-•–—]\s+/gm, '') // Remove indented list markers
        .replace(/#{1,6}\s+/g, '') // Remove markdown headers
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links, keep text
        .replace(/`([^`]+)`/g, '$1') // Remove inline code, keep text
        .replace(/_{1,2}([^_]+)_{1,2}/g, '$1') // Remove underline emphasis
        .trim();
    };
    
    return (
      <Box bg={bgColor} color={textColor} p={6} minH="100vh" className="accessible-content">
        {/* Skip to main content link for screen readers */}
        <Box as="a" href="#main-content" className="sr-only" _focus={{
          position: 'static',
          width: 'auto',
          height: 'auto',
          padding: 2,
          margin: 2,
          overflow: 'visible',
          clip: 'auto'
        }}>
          Skip to main content
        </Box>
        {/* Back, Toggle, and Print buttons - fixed at top */}
        <HStack position="fixed" top={4} left={4} right={4} justifyContent="space-between" zIndex={1000} className="accessible-controls">
          <Button
            onClick={() => {
              setPrintMode('normal');
              document.body.className = '';
            }}
            size="lg"
            bg={textColor}
            color={bgColor}
            _hover={{ opacity: 0.8 }}
            className="back-button"
          >
            ← Back
          </Button>
          <Button
            onClick={toggleHighContrast}
            size="lg"
            bg={textColor}
            color={bgColor}
            _hover={{ opacity: 0.8 }}
          >
            {isHighContrast ? '◐ Black on White' : '◑ White on Black'}
          </Button>
          <Button
            onClick={handlePrint}
            size="lg"
            bg={textColor}
            color={bgColor}
            _hover={{ opacity: 0.8 }}
            className="print-button"
          >
            🖨 Print
          </Button>
        </HStack>

        {/* Main content */}
        <Box as="main" id="main-content" mt={20}>
          {/* Scenario Title */}
          <Heading as="h1" size="xl" mb={6} color={textColor}>
            {sanitizeText(scenario.chiefComplaint) || 'Clinical Scenario'}
          </Heading>

          {/* Scenario content as nested bulleted list */}
          <VStack align="stretch" spacing={4} fontSize="lg">
            {/* Patient Demographics */}
            {patientInfo && (patientInfo.age || patientInfo.sex || patientInfo.ethnicity || patientInfo.weight) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Patient Demographics</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {patientInfo.age && hasValue(patientInfo.age) && <ListItem>Age: {sanitizeText(patientInfo.age)}</ListItem>}
                  {patientInfo.sex && hasValue(patientInfo.sex) && <ListItem>Sex: {sanitizeText(patientInfo.sex)}</ListItem>}
                  {patientInfo.ethnicity && hasValue(patientInfo.ethnicity) && <ListItem>Ethnicity: {sanitizeText(patientInfo.ethnicity)}</ListItem>}
                  {patientInfo.weight && hasValue(patientInfo.weight) && <ListItem>Weight: {sanitizeText(patientInfo.weight)}</ListItem>}
                  {patientInfo.physicalAppearance && hasValue(patientInfo.physicalAppearance) && <ListItem>Physical Appearance: {sanitizeText(patientInfo.physicalAppearance)}</ListItem>}
                </List>
              </Box>
            )}

            {/* Scene Setup */}
            {sceneSetup && (sceneSetup.location || sceneSetup.dispatchInfo || sceneSetup.bystanders || sceneSetup.safetyIssues) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Scene Setup</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {sceneSetup.location && hasValue(sceneSetup.location) && <ListItem>Location: {sanitizeText(sceneSetup.location)}</ListItem>}
                  {sceneSetup.timeOfDay && hasValue(sceneSetup.timeOfDay) && <ListItem>Time of Day: {sanitizeText(sceneSetup.timeOfDay)}</ListItem>}
                  {sceneSetup.weather && hasValue(sceneSetup.weather) && <ListItem>Weather: {sanitizeText(sceneSetup.weather)}</ListItem>}
                  {sceneSetup.dispatchInfo && hasValue(sceneSetup.dispatchInfo) && <ListItem>Dispatch Info: {sanitizeText(sceneSetup.dispatchInfo)}</ListItem>}
                  {sceneSetup.bystanders && hasValue(sceneSetup.bystanders) && <ListItem>Bystanders: {sanitizeText(sceneSetup.bystanders)}</ListItem>}
                  {(sceneSetup.safetyIssues || sceneSetup.safetyConsiderations) && hasValue(sceneSetup.safetyIssues || sceneSetup.safetyConsiderations) && (
                    <ListItem>Safety Alert: {sanitizeText(sceneSetup.safetyIssues || sceneSetup.safetyConsiderations)}</ListItem>
                  )}
                </List>
              </Box>
            )}

            {/* Chief Complaint */}
            {scenario.chiefComplaint && hasValue(scenario.chiefComplaint) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Chief Complaint</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  <ListItem>{sanitizeText(scenario.chiefComplaint)}</ListItem>
                </List>
              </Box>
            )}

            {/* Primary Assessment */}
            {primaryAssessment && (primaryAssessment.mentalStatus || primaryAssessment.airway || primaryAssessment.breathing || primaryAssessment.circulation) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Primary Assessment</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {primaryAssessment.mentalStatus && hasValue(primaryAssessment.mentalStatus) && <ListItem>Mental Status: {sanitizeText(primaryAssessment.mentalStatus)}</ListItem>}
                  {primaryAssessment.airway && hasValue(primaryAssessment.airway) && <ListItem>Airway: {sanitizeText(primaryAssessment.airway)}</ListItem>}
                  {primaryAssessment.breathing && hasValue(primaryAssessment.breathing) && <ListItem>Breathing: {sanitizeText(primaryAssessment.breathing)}</ListItem>}
                  {primaryAssessment.circulation && hasValue(primaryAssessment.circulation) && <ListItem>Circulation: {sanitizeText(primaryAssessment.circulation)}</ListItem>}
                  {primaryAssessment.skinColor && hasValue(primaryAssessment.skinColor) && <ListItem>Skin Color: {sanitizeText(primaryAssessment.skinColor)}</ListItem>}
                  {primaryAssessment.skinTemp && hasValue(primaryAssessment.skinTemp) && <ListItem>Skin Temperature: {sanitizeText(primaryAssessment.skinTemp)}</ListItem>}
                  {primaryAssessment.skinMoisture && hasValue(primaryAssessment.skinMoisture) && <ListItem>Skin Moisture: {sanitizeText(primaryAssessment.skinMoisture)}</ListItem>}
                </List>
              </Box>
            )}

            {/* Vital Signs */}
            {vitals.initial && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Initial Vital Signs</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {vitals.initial.hr && formatVital(vitals.initial.hr) && <ListItem>Heart Rate: {sanitizeText(formatVital(vitals.initial.hr))}</ListItem>}
                  {vitals.initial.bp && formatVital(vitals.initial.bp) && <ListItem>Blood Pressure: {sanitizeText(formatVital(vitals.initial.bp))}</ListItem>}
                  {vitals.initial.rr && formatVital(vitals.initial.rr) && <ListItem>Respiratory Rate: {sanitizeText(formatVital(vitals.initial.rr))}</ListItem>}
                  {vitals.initial.spo2 && formatVital(vitals.initial.spo2) && <ListItem>SpO2: {sanitizeText(formatVital(vitals.initial.spo2))}</ListItem>}
                  {vitals.initial.temp && formatVital(vitals.initial.temp) && <ListItem>Temperature: {sanitizeText(formatVital(vitals.initial.temp))}</ListItem>}
                  {vitals.initial.bloodGlucose && formatVital(vitals.initial.bloodGlucose) && <ListItem>Blood Glucose: {sanitizeText(formatVital(vitals.initial.bloodGlucose))}</ListItem>}
                  {vitals.initial.etco2 && formatVital(vitals.initial.etco2) && <ListItem>EtCO2: {sanitizeText(formatVital(vitals.initial.etco2))}</ListItem>}
                  {vitals.initial.gcs && hasValue(vitals.initial.gcs) && <ListItem>GCS: {sanitizeText(vitals.initial.gcs)}</ListItem>}
                  {vitals.initial.rhythm && formatVital(vitals.initial.rhythm) && <ListItem>Cardiac Rhythm: {sanitizeText(formatVital(vitals.initial.rhythm))}</ListItem>}
                  {vitals.initial.pupils && hasValue(vitals.initial.pupils) && <ListItem>Pupils: {sanitizeText(vitals.initial.pupils)}</ListItem>}
                </List>
              </Box>
            )}

            {/* SAMPLE History */}
            {history && (history.allergies || history.medications || history.pmhx || history.lastMeal || history.events) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>SAMPLE History</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {scenario.chiefComplaint && hasValue(scenario.chiefComplaint) && <ListItem>Symptoms: {sanitizeText(scenario.chiefComplaint)}</ListItem>}
                  {history.allergies && hasValue(history.allergies) && <ListItem>Allergies: {sanitizeText(history.allergies)}</ListItem>}
                  {history.medications && hasValue(history.medications) && <ListItem>Medications: {sanitizeText(history.medications)}</ListItem>}
                  {history.pmhx && hasValue(history.pmhx) && <ListItem>Past Medical History: {sanitizeText(history.pmhx)}</ListItem>}
                  {history.lastMeal && hasValue(history.lastMeal) && <ListItem>Last Oral Intake: {sanitizeText(history.lastMeal)}</ListItem>}
                  {history.events && hasValue(history.events) && <ListItem>Events: {sanitizeText(history.events)}</ListItem>}
                </List>
              </Box>
            )}

            {/* History of Present Illness */}
            {(history.hpi || scenario.enhancedHpi) && (hasValue(history.hpi) || hasValue(scenario.enhancedHpi)) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>History of Present Illness</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  <ListItem>{sanitizeText(scenario.enhancedHpi || history.hpi)}</ListItem>
                </List>
              </Box>
            )}

            {/* Secondary Assessment */}
            {secondaryAssessment && Object.keys(secondaryAssessment).some(key => hasValue(secondaryAssessment[key])) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Secondary Assessment</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {Object.entries(secondaryAssessment).map(([key, value]) => {
                    if (value && hasValue(value)) {
                      const label = key.toUpperCase() === 'HEENT' ? 'HEENT' : 
                                   key.toUpperCase() === 'GI' ? 'GI/Abdomen' :
                                   key.charAt(0).toUpperCase() + key.slice(1);
                      return <ListItem key={key}>{label}: {sanitizeText(value)}</ListItem>;
                    }
                    return null;
                  })}
                </List>
              </Box>
            )}

            {/* Interventions */}
            {interventions && (interventions.LifeSaving?.length > 0 || interventions.Secondary?.length > 0 || interventions.Optional?.length > 0) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Interventions</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {interventions.LifeSaving && interventions.LifeSaving.length > 0 && (
                    <ListItem>
                      <Heading as="h3" size="md" fontWeight="semibold" color={textColor}>Critical/Life-Saving Interventions:</Heading>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {interventions.LifeSaving.map((item, i) => <ListItem key={i}>{sanitizeText(item)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                  {interventions.Secondary && interventions.Secondary.length > 0 && (
                    <ListItem>
                      <Heading as="h3" size="md" fontWeight="semibold" color={textColor}>Secondary Interventions:</Heading>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {interventions.Secondary.map((item, i) => <ListItem key={i}>{sanitizeText(item)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                  {interventions.Optional && interventions.Optional.length > 0 && (
                    <ListItem>
                      <Heading as="h3" size="md" fontWeight="semibold" color={textColor}>Optional/Conditional Interventions:</Heading>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {interventions.Optional.map((item, i) => <ListItem key={i}>{sanitizeText(item)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            {/* Expected Outcome */}
            {outcome && (outcome.finalCondition || outcome.disposition || outcome.continuingCareNeeds) && (
              <Box>
                <Text fontWeight="bold" fontSize="xl">Expected Outcome</Text>
                <List styleType="disc" ml={6} mt={2}>
                  {outcome.finalCondition && hasValue(outcome.finalCondition) && <ListItem>Final Condition: {outcome.finalCondition}</ListItem>}
                  {outcome.disposition && hasValue(outcome.disposition) && <ListItem>Disposition: {outcome.disposition}</ListItem>}
                  {outcome.continuingCareNeeds && hasValue(outcome.continuingCareNeeds) && <ListItem>Continuing Care: {outcome.continuingCareNeeds}</ListItem>}
                </List>
              </Box>
            )}

            {/* Trending Vital Signs */}
            {vitals.trending && vitals.trending.length > 0 && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Trending Vital Signs</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {vitals.trending.map((vitalSet, index) => (
                    <ListItem key={index}>
                      <Text fontWeight="semibold">{vitalSet.time && hasValue(vitalSet.time) ? sanitizeText(vitalSet.time) : `Time ${index + 1}`}:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {vitalSet.hr && formatVital(vitalSet.hr) && <ListItem>Heart Rate: {sanitizeText(formatVital(vitalSet.hr))}</ListItem>}
                        {vitalSet.bp && formatVital(vitalSet.bp) && <ListItem>Blood Pressure: {sanitizeText(formatVital(vitalSet.bp))}</ListItem>}
                        {vitalSet.rr && formatVital(vitalSet.rr) && <ListItem>Respiratory Rate: {sanitizeText(formatVital(vitalSet.rr))}</ListItem>}
                        {vitalSet.spo2 && formatVital(vitalSet.spo2) && <ListItem>SpO2: {sanitizeText(formatVital(vitalSet.spo2))}</ListItem>}
                        {vitalSet.temp && formatVital(vitalSet.temp) && <ListItem>Temperature: {sanitizeText(formatVital(vitalSet.temp))}</ListItem>}
                        {vitalSet.bloodGlucose && formatVital(vitalSet.bloodGlucose) && <ListItem>Blood Glucose: {sanitizeText(formatVital(vitalSet.bloodGlucose))}</ListItem>}
                        {vitalSet.etco2 && formatVital(vitalSet.etco2) && <ListItem>EtCO2: {sanitizeText(formatVital(vitalSet.etco2))}</ListItem>}
                        {vitalSet.gcs && hasValue(vitalSet.gcs) && <ListItem>GCS: {sanitizeText(vitalSet.gcs)}</ListItem>}
                      </List>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Progression With Treatment */}
            {scenario.vitalSigns?.progressionWithProperTreatment && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Progression WITH Proper Treatment</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {scenario.vitalSigns.progressionWithProperTreatment.description && hasValue(scenario.vitalSigns.progressionWithProperTreatment.description) && (
                    <ListItem>Description: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.description)}</ListItem>
                  )}
                  {scenario.vitalSigns.progressionWithProperTreatment.timeframe && hasValue(scenario.vitalSigns.progressionWithProperTreatment.timeframe) && (
                    <ListItem>Timeframe: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.timeframe)}</ListItem>
                  )}
                  {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals && (
                    <ListItem>
                      <Text>Expected Vitals:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.hr && <ListItem>Heart Rate: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.hr)}</ListItem>}
                        {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.bp && <ListItem>Blood Pressure: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.bp)}</ListItem>}
                        {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.rr && <ListItem>Respiratory Rate: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.rr)}</ListItem>}
                        {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.spo2 && <ListItem>SpO2: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.spo2)}</ListItem>}
                        {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.gcs && <ListItem>GCS: {sanitizeText(scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.gcs)}</ListItem>}
                      </List>
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            {/* Deterioration Without Treatment */}
            {scenario.vitalSigns?.progressionWithoutTreatment && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Deterioration WITHOUT Treatment</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {scenario.vitalSigns.progressionWithoutTreatment.description && hasValue(scenario.vitalSigns.progressionWithoutTreatment.description) && (
                    <ListItem>Description: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.description)}</ListItem>
                  )}
                  {scenario.vitalSigns.progressionWithoutTreatment.timeframe && hasValue(scenario.vitalSigns.progressionWithoutTreatment.timeframe) && (
                    <ListItem>Timeframe: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.timeframe)}</ListItem>
                  )}
                  {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals && (
                    <ListItem>
                      <Text>Expected Vitals:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.hr && <ListItem>Heart Rate: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.hr)}</ListItem>}
                        {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.bp && <ListItem>Blood Pressure: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.bp)}</ListItem>}
                        {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.rr && <ListItem>Respiratory Rate: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.rr)}</ListItem>}
                        {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.spo2 && <ListItem>SpO2: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.spo2)}</ListItem>}
                        {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.gcs && <ListItem>GCS: {sanitizeText(scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.gcs)}</ListItem>}
                      </List>
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            {/* Learning Objectives */}
            {learning.learningObjectives && learning.learningObjectives.length > 0 && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Learning Objectives</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {learning.learningObjectives.map((objective, i) => <ListItem key={i}>{sanitizeText(objective)}</ListItem>)}
                </List>
              </Box>
            )}

            {/* Transport Context (FP-C/CCP-C) */}
            {transportContext && hasValue(transportContext) && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Transport Context</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  <ListItem>{sanitizeText(transportContext)}</ListItem>
                </List>
              </Box>
            )}

            {/* Definitive Care */}
            {hasDefinitiveCare && (
              <Box>
                <Heading as="h2" size="lg" fontWeight="bold" mt={4} mb={2} color={textColor}>Hospital Definitive Care</Heading>
                <List as="ul" styleType="disc" ml={6} mt={2} spacing={1}>
                  {definitiveCare.labTests && definitiveCare.labTests.length > 0 && (
                    <ListItem>
                      <Text fontWeight="semibold">Laboratory Results:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {definitiveCare.labTests.map((test, i) => <ListItem key={i}>{sanitizeText(test)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                  {definitiveCare.imagingStudies && definitiveCare.imagingStudies.length > 0 && (
                    <ListItem>
                      <Text fontWeight="semibold">Imaging Studies:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {definitiveCare.imagingStudies.map((study, i) => <ListItem key={i}>{sanitizeText(study)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                  {definitiveCare.diagnosticProcedures && definitiveCare.diagnosticProcedures.length > 0 && (
                    <ListItem>
                      <Text fontWeight="semibold">Diagnostic Procedures:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {definitiveCare.diagnosticProcedures.map((proc, i) => <ListItem key={i}>{sanitizeText(proc)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                  {definitiveCare.definitiveInterventions && definitiveCare.definitiveInterventions.length > 0 && (
                    <ListItem>
                      <Text fontWeight="semibold">Definitive Treatment:</Text>
                      <List as="ul" styleType="circle" ml={6} mt={1} spacing={1}>
                        {definitiveCare.definitiveInterventions.map((intervention, i) => <ListItem key={i}>{sanitizeText(intervention)}</ListItem>)}
                      </List>
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            {/* Critical Actions */}
            {learning.criticalActions && learning.criticalActions.length > 0 && (
              <Box>
                <Text fontWeight="bold" fontSize="xl">Critical Actions</Text>
                <List styleType="disc" ml={6} mt={2}>
                  {learning.criticalActions.map((action, i) => <ListItem key={i}>{action}</ListItem>)}
                </List>
              </Box>
            )}

            {/* Common Pitfalls */}
            {learning.commonPitfalls && learning.commonPitfalls.length > 0 && (
              <Box>
                <Text fontWeight="bold" fontSize="xl">Common Pitfalls</Text>
                <List styleType="disc" ml={6} mt={2}>
                  {learning.commonPitfalls.map((pitfall, i) => <ListItem key={i}>{pitfall}</ListItem>)}
                </List>
              </Box>
            )}

            {/* Differential Diagnosis */}
            {learning.differentialDiagnosis && learning.differentialDiagnosis.length > 0 && (
              <Box>
                <Text fontWeight="bold" fontSize="xl">Differential Diagnosis</Text>
                <List styleType="disc" ml={6} mt={2}>
                  {learning.differentialDiagnosis.map((dx, i) => {
                    if (typeof dx === 'string') {
                      return <ListItem key={i}>{dx}</ListItem>;
                    } else {
                      return <ListItem key={i}>{dx.condition || dx.Condition}</ListItem>;
                    }
                  })}
                </List>
              </Box>
            )}
          </VStack>
        </Box>
      </Box>
    );
  };

  return (
    <Box position="relative">
      <style>{printStyles}</style>
      <style>{`
        /* Ensure watermarks print */
        .scenario-watermark,
        .subtle-watermark {
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        
        @media print {
          .scenario-watermark {
            opacity: 0.25 !important;
          }
        }
      `}</style>



      {/* Accessible Content - Hidden in other modes */}
      {printMode === 'accessible' && renderAccessibleContent()}



      {/* Visual Content - Hidden in accessible mode */}
      {printMode !== 'accessible' && (
        <>
          {/* Tablet Mode Controls - Translucent overlay */}
          {printMode === 'tablet-view' && (
        <Box 
          className="tablet-controls" 
          position="fixed" 
          top={4} 
          right={4} 
          zIndex={9999}
          bg="blackAlpha.300"
          borderRadius="md"
          p={1}
          opacity={0.4}
          _hover={{ opacity: 0.95, bg: 'blackAlpha.500' }}
          transition="all 0.2s"
        >
          <VStack spacing={1}>
            <Button 
              size="xs" 
              colorScheme="red" 
              onClick={exitTabletMode}
              variant="solid"
            >
              Exit
            </Button>
            <Button 
              size="xs" 
              colorScheme={orientationMode === 'auto' ? 'blue' : 'gray'}
              onClick={() => setOrientation('auto')}
              variant="solid"
            >
              Auto
            </Button>
            <Button 
              size="xs" 
              colorScheme={orientationMode === 'landscape' ? 'blue' : 'gray'}
              onClick={() => setOrientation('landscape')}
              variant="solid"
            >
              ⟷
            </Button>
            <Button 
              size="xs" 
              colorScheme={orientationMode === 'portrait' ? 'blue' : 'gray'}
              onClick={() => setOrientation('portrait')}
              variant="solid"
            >
              ⬍
            </Button>
          </VStack>
        </Box>
      )}



      {/* Tablet View Controls - Only visible in tablet mode */}
      {printMode === 'tablet-view' && (
        <Box className="tablet-controls" position="fixed" top={4} right={4} zIndex={9999} bg="white" p={2} borderRadius="md" shadow="lg" opacity={0.4} _hover={{ opacity: 0.95 }}>
          <VStack spacing={2}>
            <Button
              size="xs"
              colorScheme="red"
              onClick={() => {
                setPrintMode('normal');
                exitFullscreen();
              }}
            >
              Exit
            </Button>
            <Button
              size="xs"
              colorScheme="blue"
              onClick={() => {
                if (orientationMode === 'auto') {
                  setOrientationMode('landscape');
                  setIsLandscape(true);
                } else if (orientationMode === 'landscape') {
                  setOrientationMode('portrait');
                  setIsLandscape(false);
                } else {
                  setOrientationMode('auto');
                  setIsLandscape(window.innerWidth > window.innerHeight);
                }
              }}
            >
              {orientationMode === 'landscape' ? '📱 Port' : '🖥️ Land'}
            </Button>
            <Button
              size="xs"
              colorScheme="green"
              onClick={() => {
                // Don't exit tablet mode - just print what's on screen
                window.print();
              }}
              title="Print current tablet view"
            >
              🖨️ Print
            </Button>
          </VStack>
        </Box>
      )}

      {/* Print Mode Selector - Top Only */}
      {printMode !== 'accessible' && printMode !== 'tablet-view' && (
        <Box className="no-print scenario-header avoid-break" mb={4}>
          <HStack spacing={4} justify="space-between" wrap="wrap">
            <HStack spacing={2}>
              <Text fontWeight="bold" fontSize="sm">View Mode:</Text>
              <Button
                size="sm"
                colorScheme={printMode === 'normal' ? 'blue' : 'gray'}
                onClick={() => handlePrintMode('normal')}
                variant={printMode === 'normal' ? 'solid' : 'outline'}
              >
                Normal View
              </Button>
              <Button
                size="sm"
                colorScheme={printMode === 'simple-print' ? 'blue' : 'gray'}
                onClick={() => handlePrintMode('simple-print')}
                variant={printMode === 'simple-print' ? 'solid' : 'outline'}
              >
                Simple (1 Page)
              </Button>
              <Button
                size="sm"
                colorScheme="teal"
                onClick={() => handlePrintMode('tablet-view')}
              >
                Tablet View
              </Button>
              <Button
                size="sm"
                colorScheme="purple"
                onClick={() => handlePrintMode('accessible')}
              >
                Accessible Mode
              </Button>
            </HStack>
          </HStack>
        </Box>
      )}

      {/* Print Instructions for Simple Mode */}
      {printMode === 'simple-print' && (
        <Box 
          position="fixed" 
          top="8px" 
          right="8px" 
          bg="white" 
          border="3px solid black" 
          p={3} 
          borderRadius="md"
          zIndex={10000}
          className="print-button"
          shadow="xl"
        >
          <VStack spacing={2} align="stretch">
            <Text fontWeight="bold" fontSize="sm" color="black">Simple Print Mode</Text>
            <Text fontSize="xs" color="black">Review on screen, then click Print button</Text>
            <Button onClick={handleSaveSimplePDF} colorScheme="blue" size="sm">
              🖨️ Print Now
            </Button>
            <Button onClick={() => handlePrintMode('normal')} size="sm" variant="outline">
              ← Back to Normal
            </Button>
          </VStack>
        </Box>
      )}

      {/* Accessibility mode contrast toggle - only when IN accessible mode */}
      {printMode === 'accessible' && (
        <Box className="no-print" bg="gray.100" p={3} mb={3} borderRadius="md" border="2px solid" borderColor="purple.500">
          <HStack spacing={2} flexWrap="wrap">
            <Button
              size="sm"
              colorScheme={isHighContrast ? 'purple' : 'gray'}
              onClick={toggleHighContrast}
              leftIcon={<Box as="span">{isHighContrast ? '⚫' : '⚪'}</Box>}
            >
              {isHighContrast ? 'White on Black' : 'Black on White'}
            </Button>
          </HStack>
        </Box>
      )}

      {/* Raw Generator Input Section - Show user's original form data - ADMIN ONLY */}
      {printMode !== 'accessible' && isAdmin() && (
        <>
          <Box className="no-print" mt={2} mb={2}>
            <label>
              <input
                type="checkbox"
                checked={showRawUserInput}
                onChange={(e) => setShowRawUserInput(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Show raw generator input (user's form data)
            </label>
          </Box>
          
          {showRawUserInput && (
            <Card bg={cardBg} shadow="md" color={cardTextColor} mt={2} mb={3} className="no-print">
              <CardHeader bg="gray.600" color="white" borderTopRadius="md">
                <Heading size="md" color="white">Raw Generator Input (User's Form Data)</Heading>
              </CardHeader>
              <CardBody>
                <Text fontSize="sm" mb={2} color={labelColor}>
                  This shows the original parameters that were entered into the scenario generator form before the AI created the scenario.
                </Text>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', overflow: 'auto' }}>
                  {JSON.stringify(metadata, null, 2)}
                </pre>
              </CardBody>
            </Card>
          )}
        </>
      )}

      {/* Patient Info Banner - Always Visible */}
      <Box 
        bg={useColorModeValue('gray.100', 'gray.900')} 
        borderBottom="2px solid" 
        borderColor="blue.500"
        p={2}
        mb={2}
        className="patient-info-banner"
      >
        <HStack spacing={4} justify="center" flexWrap="wrap">
          {/* Training Level - from metadata */}
          {showRawInput && metadata?.levelOfCare && hasValue(metadata.levelOfCare) && (
            <Text fontSize="sm" fontWeight="bold" color="orange.600">
              Level: {metadata.levelOfCare.replace(/_/g, ' ')}
            </Text>
          )}
          {/* Patient Age */}
          {patientInfo.age && hasValue(patientInfo.age) && (
            <Text fontSize="sm" fontWeight="bold">
              Age: {patientInfo.age}
            </Text>
          )}
          {/* Chief Complaint */}
          {scenario.chiefComplaint && hasValue(scenario.chiefComplaint) && (
            <Text fontSize="sm" fontWeight="bold">
              CC: {scenario.chiefComplaint}
            </Text>
          )}
          {/* MOI/NOI - from metadata */}
          {showRawInput && metadata?.mechanismNatureOfInjury && hasValue(metadata.mechanismNatureOfInjury) && (
            <Text fontSize="sm" fontWeight="bold" color="orange.600">
              MOI/NOI: {metadata.mechanismNatureOfInjury}
            </Text>
          )}
        </HStack>
      </Box>

      <Box maxW="1200px" mx="auto" p={{ base: 2, md: 3 }} bg={cardBg} color={cardTextColor} className="scenario-content-for-pdf">

      {/* Generated Media Section */}
      <MediaDisplaySection scenario={scenario} />

      {/* Preceptor Notes & Scenario Flow - FIRST, FULL WIDTH */}
      {(hasProctorNotes || scenario.narrativeArc) && (
        <Card bg={cardBg} shadow="md" color={cardTextColor} mb={2} className="page-break-avoid">
          <CardHeader bg="teal.600" color="white" borderTopRadius="md" py={1}>
            <Heading size="sm" color="white">Preceptor Notes & Scenario Flow</Heading>
          </CardHeader>
          <CardBody p={1}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
              {/* Left Column: Scenario Flow */}
              {scenario.narrativeArc && (
                <Box>
                  <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Scenario Flow:</Text>
                  {scenario.narrativeArc.fullNarrative ? (
                    <Text fontSize="sm" lineHeight="tall" color={textColor} fontStyle="italic">
                      {scenario.narrativeArc.fullNarrative}
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing={1}>
                      {scenario.narrativeArc.arrival && (
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" color="blue.600">Arrival:</Text>
                          <Text fontSize="sm" color={textColor}>{scenario.narrativeArc.arrival}</Text>
                        </Box>
                      )}
                      {scenario.narrativeArc.keyTurningPoint && (
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" color="orange.600">Key Turning Point:</Text>
                          <Text fontSize="sm" color={textColor}>{scenario.narrativeArc.keyTurningPoint}</Text>
                        </Box>
                      )}
                      {scenario.narrativeArc.intervention && (
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" color="green.600">Intervention:</Text>
                          <Text fontSize="sm" color={textColor}>{scenario.narrativeArc.intervention}</Text>
                        </Box>
                      )}
                      {scenario.narrativeArc.resolution && (
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" color="purple.600">Resolution:</Text>
                          <Text fontSize="sm" color={textColor}>{scenario.narrativeArc.resolution}</Text>
                        </Box>
                      )}
                    </VStack>
                  )}
                </Box>
              )}
              
              {/* Right Column: Preceptor Notes */}
              {hasProctorNotes && (
                <Box>
                  <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Preceptor Guidance:</Text>
                  <VStack align="stretch" spacing={1}>
                    {proctorNotes.timingGuidance && hasValue(proctorNotes.timingGuidance) && (
                      <Box bg={boxBg} p={2} borderRadius="md" borderLeft="4px solid" borderColor="blue.500">
                        <Text fontWeight="bold" color="blue.600" fontSize="sm" mb={0.5}>⏱️ Timing Guidance</Text>
                        <Text fontSize="sm" color={textColor}>{proctorNotes.timingGuidance}</Text>
                      </Box>
                    )}
                    {proctorNotes.criticalTimings && hasValue(proctorNotes.criticalTimings) && (
                      <Box bg={boxBg} p={2} borderRadius="md" borderLeft="4px solid" borderColor="red.500">
                        <Text fontWeight="bold" color="red.600" fontSize="sm" mb={0.5}>⚡ Critical Timings</Text>
                        <Text fontSize="sm" color={textColor}>{proctorNotes.criticalTimings}</Text>
                      </Box>
                    )}
                    {proctorNotes.progressionCues && hasValue(proctorNotes.progressionCues) && (
                      <Box bg={boxBg} p={2} borderRadius="md" borderLeft="4px solid" borderColor="green.500">
                        <Text fontWeight="bold" color="green.600" fontSize="sm" mb={0.5}>➡️ Progression Cues</Text>
                        <Text fontSize="sm" color={textColor}>{proctorNotes.progressionCues}</Text>
                      </Box>
                    )}
                  </VStack>
                </Box>
              )}
              
              {/* Full-width if only one column has content */}
              {!scenario.narrativeArc && hasProctorNotes && <Box />}
              {scenario.narrativeArc && !hasProctorNotes && <Box />}
            </SimpleGrid>
          </CardBody>
        </Card>
      )}


      {/* Row 1: Background/Dispatch and Patient History - 2 Columns */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1} mb={1} className="first-row-grid page-break-avoid">
        {/* Background / Dispatch */}
        <Card bg={cardBg} shadow="md" color={cardTextColor} className="page-break-avoid">
          <CardHeader bg="blue.600" color="white" borderTopRadius="md" py={1}>
            <Heading size="sm" color="white">
              <HStack><FaMapMarkerAlt /><Text>Background / Dispatch</Text></HStack>
            </Heading>
          </CardHeader>
          <CardBody p={1}>
            <VStack align="stretch" spacing={1}>
              {/* Dispatch Info - NOW FIRST */}
              {sceneSetup.dispatchInfo && hasValue(sceneSetup.dispatchInfo) && (
                <Box bg={boxBg} p={2} borderRadius="md">
                  <Text fontWeight="bold" fontSize="sm" color={boldTextColor}>Dispatch Info:</Text>
                  {scenario.sceneArrivalVideoUrl ? (
                    <Text 
                      fontSize="md" 
                      color="blue.500" 
                      textDecoration="underline" 
                      cursor="pointer"
                      onClick={() => openMediaModal('video', scenario.sceneArrivalVideoUrl, 'Scene Arrival Video')}
                      _hover={{ color: 'blue.600' }}
                    >
                      {sceneSetup.dispatchInfo} 🎥
                    </Text>
                  ) : (
                    <Text fontSize="md" color={textColor}>{sceneSetup.dispatchInfo}</Text>
                  )}
                </Box>
              )}
              
              {/* Flight Operations - FP-C/CCP-C ONLY */}
              {sceneSetup.flightOperations && hasValue(sceneSetup.flightOperations) && (
                <Box bg={boxBg} p={2} borderRadius="md">
                  <HStack spacing={2} mb={1}>
                    <Box as={FaHelicopter} color="orange.500" />
                    <Text fontWeight="bold" fontSize="sm" color="orange.500">Flight Operations Considerations</Text>
                  </HStack>
                  <Box bg={alertBgWarning} p={2} borderRadius="md" borderLeft="4px solid" borderColor="orange.500">
                    <Text fontSize="sm" color={textColor} whiteSpace="pre-line">{sceneSetup.flightOperations}</Text>
                  </Box>
                </Box>
              )}
              
              {/* Patient Information - SECOND */}
              {(patientInfo.name || patientInfo.age || patientInfo.sex || patientInfo.weight) && 
               (hasValue(patientInfo.name) || hasValue(patientInfo.age) || hasValue(patientInfo.sex) || hasValue(patientInfo.weight)) && (
                <Box bg={boxBg} p={2} borderRadius="md">
                  <Text color={textColor} fontSize="md">
                    <Text as="span" fontWeight="bold">Patient: </Text>
                    {[
                      patientInfo.age && hasValue(patientInfo.age) ? patientInfo.age : null,
                      patientInfo.sex && hasValue(patientInfo.sex) ? patientInfo.sex : null,
                      patientInfo.weight && hasValue(patientInfo.weight) ? patientInfo.weight : null
                    ].filter(Boolean).join(', ')}
                    {patientInfo.name && hasValue(patientInfo.name) && ` (${patientInfo.name})`}
                  </Text>
                  {(patientInfo.physicalAppearance || patientInfo.moulage || moulage) && (hasValue(patientInfo.physicalAppearance) || hasValue(patientInfo.moulage) || hasValue(moulage)) && (
                    <Text color={textColor} fontSize="sm" mt={1}>
                      <Text as="span" fontWeight="bold">Appearance: </Text>
                      {scenario.patientImageUrl ? (
                        <Text 
                          as="span" 
                          color="blue.500" 
                          textDecoration="underline" 
                          cursor="pointer"
                          onClick={() => openMediaModal('image', scenario.patientImageUrl, 'Patient Image')}
                          _hover={{ color: 'blue.600' }}
                        >
                          {patientInfo.moulage || moulage || patientInfo.physicalAppearance} 📷
                        </Text>
                      ) : (
                        patientInfo.moulage || moulage || patientInfo.physicalAppearance
                      )}
                    </Text>
                  )}
                </Box>
              )}
              
              {/* Time, Location, Weather - THIRD */}
              {(sceneSetup.timeOfDay || sceneSetup.location || sceneSetup.weather) && 
               (hasValue(sceneSetup.timeOfDay) || hasValue(sceneSetup.location) || hasValue(sceneSetup.weather)) && (
                <Box bg={boxBg} p={2} borderRadius="md">
                  {sceneSetup.timeOfDay && hasValue(sceneSetup.timeOfDay) && (
                    <Text color={textColor} fontSize="md">{sceneSetup.timeOfDay}</Text>
                  )}
                  {sceneSetup.location && hasValue(sceneSetup.location) && (
                    <Text color={textColor} fontSize="md">{sceneSetup.location}</Text>
                  )}
                  {sceneSetup.weather && hasValue(sceneSetup.weather) && (
                    <Text color={textColor} fontSize="md">{sceneSetup.weather}</Text>
                  )}
                </Box>
              )}
              
              {/* Safety Alert and Bystanders - LAST */}
              {((sceneSetup.safetyConsiderations || sceneSetup.safetyIssues || sceneSetup.bystanders) &&
                (hasValue(sceneSetup.safetyConsiderations) || hasValue(sceneSetup.safetyIssues) || hasValue(sceneSetup.bystanders))) && (
                <Box bg={boxBg} p={2} borderRadius="md">
                  {(sceneSetup.safetyConsiderations || sceneSetup.safetyIssues) && hasValue(sceneSetup.safetyConsiderations || sceneSetup.safetyIssues) && (
                    <>
                      <Text fontWeight="bold" fontSize="sm" color="red.600">⚠ Safety Alert:</Text>
                      <Text fontSize="md" color={textColor} mb={sceneSetup.bystanders && hasValue(sceneSetup.bystanders) ? 1 : 0}>
                        {sceneSetup.safetyConsiderations || sceneSetup.safetyIssues}
                      </Text>
                    </>
                  )}
                  {sceneSetup.bystanders && hasValue(sceneSetup.bystanders) && (
                    <>
                      <Text fontWeight="bold" color={boldTextColor} fontSize="sm" mt={(sceneSetup.safetyConsiderations || sceneSetup.safetyIssues) ? 1 : 0}>
                        Bystanders/Scene:
                      </Text>
                      <Text color={textColor} fontSize="sm">{sceneSetup.bystanders}</Text>
                    </>
                  )}
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Patient History */}
        <Card bg={cardBg} shadow="md" color={cardTextColor} className="page-break-avoid">
          <CardHeader bg="purple.600" color="white" borderTopRadius="md" py={1}>
            <Heading size="sm" color="white">Patient History</Heading>
          </CardHeader>
          <CardBody p={1}>
            <VStack align="stretch" spacing={1}>
              {/* S - Signs/Symptoms (Chief Complaint) */}
              {scenario.chiefComplaint && hasValue(scenario.chiefComplaint) && (
                <CheckableItem
                  evaluationMode={evaluationMode}
                  isChecked={isChecked('primary', 'chiefComplaint')}
                  onToggle={(checked) => handleCheckboxToggle('primary', 'chiefComplaint', checked)}
                  label="Chief Complaint"
                >
                  <Box bg={boxBg} p={2} borderRadius="md">
                    <Text fontWeight="bold" color={boldTextColor} fontSize="sm" mb={1}>Chief Complaint:</Text>
                    <Text fontSize="sm" fontWeight="semibold" color={textColor}>{scenario.chiefComplaint}</Text>
                  </Box>
                </CheckableItem>
              )}

              {/* HPI with OPQRST details */}
              {(history.hpi || scenario.enhancedHpi) && (hasValue(history.hpi) || hasValue(scenario.enhancedHpi)) && (
                <Box bg={boxBg} p={2} borderRadius="md">
                  <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">History of Present Illness:</Text>
                  <Text fontSize="sm" lineHeight="short" color={textColor}>
                    {scenario.enhancedHpi || history.hpi}
                  </Text>
                  {scenario.enhancedHpi && (
                    <Badge colorScheme="cyan" size="sm" mt={1} display="inline-block">
                      ✨ Enhanced for {scenario.ScenarioMetadata?.TrainingLevel || scenario.trainingLevel} level
                    </Badge>
                  )}
                </Box>
              )}

              {/* A - Allergies */}
              {history.allergies && hasValue(history.allergies) && (
                <CheckableItem
                  evaluationMode={evaluationMode}
                  isChecked={isChecked('sample', 'allergies')}
                  onToggle={(checked) => handleCheckboxToggle('sample', 'allergies', checked)}
                  label="Allergies"
                >
                  <Alert status="error" borderRadius="md" size="sm" py={1}>
                    <AlertIcon boxSize={3} />
                    <Box flex="1">
                      <AlertTitle fontSize="xs">Allergies</AlertTitle>
                      <AlertDescription fontWeight="bold" fontSize="sm">{history.allergies}</AlertDescription>
                    </Box>
                  </Alert>
                </CheckableItem>
              )}

              {/* M - Medications */}
              {history.medications && hasValue(history.medications) && (
                <CheckableItem
                  evaluationMode={evaluationMode}
                  isChecked={isChecked('sample', 'medications')}
                  onToggle={(checked) => handleCheckboxToggle('sample', 'medications', checked)}
                  label="Medications"
                >
                  <Box bg={boxBg} p={2} borderRadius="md">
                    <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Medications:</Text>
                    <Text color={textColor} fontSize="sm">{history.medications}</Text>
                  </Box>
                </CheckableItem>
              )}

              {/* P - Pertinent PMHx */}
              {history.pmhx && hasValue(history.pmhx) && (
                <CheckableItem
                  evaluationMode={evaluationMode}
                  isChecked={isChecked('sample', 'pmhx')}
                  onToggle={(checked) => handleCheckboxToggle('sample', 'pmhx', checked)}
                  label="Past Medical History"
                >
                  <Box bg={boxBg} p={2} borderRadius="md">
                    <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Pertinent PMHx:</Text>
                    <Text color={textColor} fontSize="sm">{history.pmhx}</Text>
                  </Box>
                </CheckableItem>
              )}

              {/* L - Last Oral Intake */}
              {history.lastMeal && hasValue(history.lastMeal) && (
                <CheckableItem
                  evaluationMode={evaluationMode}
                  isChecked={isChecked('sample', 'lastMeal')}
                  onToggle={(checked) => handleCheckboxToggle('sample', 'lastMeal', checked)}
                  label="Last Oral Intake"
                >
                  <Box bg={boxBg} p={2} borderRadius="md">
                    <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Last Oral Intake:</Text>
                    <Text color={textColor} fontSize="sm">{history.lastMeal}</Text>
                  </Box>
                </CheckableItem>
              )}

              {/* E - Events Leading to Incident */}
              {history.events && hasValue(history.events) && (
                <CheckableItem
                  evaluationMode={evaluationMode}
                  isChecked={isChecked('sample', 'events')}
                  onToggle={(checked) => handleCheckboxToggle('sample', 'events', checked)}
                  label="Events"
                >
                  <Box bg={boxBg} p={2} borderRadius="md">
                    <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Events Leading to Incident:</Text>
                    <Text fontSize="sm" lineHeight="short" color={textColor}>{history.events}</Text>
                  </Box>
                </CheckableItem>
              )}

              {/* Prior to Arrival Care */}
              {sceneSetup.priorToArrivalCare && hasValue(sceneSetup.priorToArrivalCare) && sceneSetup.priorToArrivalCare !== 'None' && (
                <CheckableItem
                  evaluationMode={evaluationMode}
                  isChecked={isChecked('sample', 'pta')}
                  onToggle={(checked) => handleCheckboxToggle('sample', 'pta', checked)}
                  label="Prior to Arrival Care"
                >
                  <Box bg={alertBgInfo} p={2} borderRadius="md" borderLeft="4px solid" borderColor="purple.500">
                    <Text fontWeight="bold" color="purple.600" fontSize="sm" mb={1}>Prior to Arrival (PTA):</Text>
                    <Text fontSize="sm" color={textColor}>{sceneSetup.priorToArrivalCare}</Text>
                  </Box>
                </CheckableItem>
              )}
            </VStack>
          </CardBody>
        </Card>
      </SimpleGrid>





      {/* Row 3: Primary Assessment and Secondary Assessment - 2 Columns */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1} mb={1} className="second-row-grid page-break-avoid">
        {/* Primary Assessment with Vitals */}
        <Card bg={cardBg} shadow="md" color={cardTextColor} className="page-break-avoid">
          <CardHeader bg="green.600" color="white" borderTopRadius="md" py={1}>
            <Heading size="sm" color="white">Primary Assessment</Heading>
          </CardHeader>
          <CardBody p={1}>
            <VStack align="stretch" spacing={1}>
              {primaryAssessment.mentalStatus && hasValue(primaryAssessment.mentalStatus) && (
                <Box>
                  <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="xs">Level of Consciousness:</Text>
                  <Badge 
                    colorScheme={getLOCColor(primaryAssessment.mentalStatus)} 
                    fontSize="sm" 
                    px={2} 
                    py={1} 
                    borderRadius="md"
                  >
                    {primaryAssessment.mentalStatus}
                  </Badge>
                </Box>
              )}

              {(primaryAssessment.airway || primaryAssessment.breathing || primaryAssessment.circulation) && (
                <Box>
                  <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="xs">ABC Status:</Text>
                  <SimpleGrid columns={3} spacing={1}>
                    {primaryAssessment.airway && hasValue(primaryAssessment.airway) && (
                      <Box bg="white" borderLeft="3px solid" borderColor="red.600" p={1} borderRadius="md">
                        <HStack spacing={0.5} mb={0.5}>
                          <FaUser color="#C53030" size={10} />
                          <Text fontWeight="bold" color="red.600" fontSize="xs">AIRWAY</Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.900">{primaryAssessment.airway}</Text>
                      </Box>
                    )}
                    {primaryAssessment.breathing && hasValue(primaryAssessment.breathing) && (
                      <Box bg="white" borderLeft="3px solid" borderColor="orange.600" p={1} borderRadius="md">
                        <HStack spacing={0.5} mb={0.5}>
                          <FaLungs color="#C05621" size={10} />
                          <Text fontWeight="bold" color="orange.600" fontSize="xs">BREATHING</Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.900">{primaryAssessment.breathing}</Text>
                      </Box>
                    )}
                    {primaryAssessment.circulation && hasValue(primaryAssessment.circulation) && (
                      <Box bg="white" borderLeft="3px solid" borderColor="red.600" p={1} borderRadius="md">
                        <HStack spacing={0.5} mb={0.5}>
                          <FaHeartbeat color="#C53030" size={10} />
                          <Text fontWeight="bold" color="red.600" fontSize="xs">CIRCULATION</Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.900">{primaryAssessment.circulation}</Text>
                        {(primaryAssessment.skinColor || primaryAssessment.skinTemp || primaryAssessment.skinMoisture) && (
                          <Text fontSize="xs" color="gray.700" mt={1}>
                            Skin: {[
                              primaryAssessment.skinColor && hasValue(primaryAssessment.skinColor) ? primaryAssessment.skinColor : null,
                              primaryAssessment.skinTemp && hasValue(primaryAssessment.skinTemp) ? primaryAssessment.skinTemp : null,
                              primaryAssessment.skinMoisture && hasValue(primaryAssessment.skinMoisture) ? primaryAssessment.skinMoisture : null
                            ].filter(Boolean).join(', ')}
                          </Text>
                        )}
                      </Box>
                    )}
                    </SimpleGrid>
                  </Box>
              )}

              {/* Initial Vital Signs */}
              {vitals.initial && (
                <CheckableItem
                  evaluationMode={evaluationMode}
                  isChecked={isChecked('vitals', 'initial')}
                  onToggle={(checked) => handleCheckboxToggle('vitals', 'initial', checked)}
                  label="Vital Signs"
                >
                  <Box>
                    <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="xs" mt={1}>Initial Vital Signs:</Text>
                    <SimpleGrid columns={5} spacing={1}>
                    {vitals.initial.hr && formatVital(vitals.initial.hr) && (
                      <Stat size="sm">
                        <StatLabel fontSize="xs" color={labelColor}>HR</StatLabel>
                        <StatNumber color={getVitalColor(getVitalStatus('hr', vitals.initial.hr))} fontSize="md">
                          {formatVital(vitals.initial.hr)}
                        </StatNumber>
                      </Stat>
                    )}
                    {vitals.initial.bp && formatVital(vitals.initial.bp) && (
                      <Stat size="sm">
                        <StatLabel fontSize="xs" color={labelColor}>BP</StatLabel>
                        <StatNumber color={getVitalColor(getBPStatus(vitals.initial.bp))} fontSize="md">
                          {formatVital(vitals.initial.bp)}
                        </StatNumber>
                      </Stat>
                    )}
                    {vitals.initial.rr && formatVital(vitals.initial.rr) && (
                      <Stat size="sm">
                        <StatLabel fontSize="xs" color={labelColor}>RR</StatLabel>
                        <StatNumber color={getVitalColor(getVitalStatus('rr', vitals.initial.rr))} fontSize="md">
                          {formatVital(vitals.initial.rr)}
                        </StatNumber>
                      </Stat>
                    )}
                    {vitals.initial.spo2 && formatVital(vitals.initial.spo2) && (
                      <Stat size="sm">
                        <StatLabel fontSize="xs" color={labelColor}>SpO₂</StatLabel>
                        <StatNumber color={getVitalColor(getVitalStatus('spo2', vitals.initial.spo2))} fontSize="md">
                          {formatVital(vitals.initial.spo2)}
                        </StatNumber>
                      </Stat>
                    )}
                    {vitals.initial.temp && formatVital(vitals.initial.temp) && (
                      <Stat size="sm">
                        <StatLabel fontSize="xs" color={labelColor}>Temp</StatLabel>
                        <StatNumber color={getVitalColor(getVitalStatus('temp', vitals.initial.temp))} fontSize="md">
                          {formatVital(vitals.initial.temp)}
                        </StatNumber>
                      </Stat>
                    )}
                    {vitals.initial.bloodGlucose && formatVital(vitals.initial.bloodGlucose) && (
                      <Stat size="sm">
                        <StatLabel fontSize="xs" color={labelColor}>BGL</StatLabel>
                        <StatNumber color={getVitalColor(getVitalStatus('bloodGlucose', vitals.initial.bloodGlucose))} fontSize="md">
                          {formatVital(vitals.initial.bloodGlucose)}
                        </StatNumber>
                      </Stat>
                    )}
                    {vitals.initial.rhythm && formatVital(vitals.initial.rhythm) && (
                      <Stat size="sm">
                        <StatLabel fontSize="xs" color={labelColor}>Rhythm</StatLabel>
                        <StatNumber fontSize="sm" color={cardTextColor}>{formatVital(vitals.initial.rhythm)}</StatNumber>
                      </Stat>
                    )}
                    {vitals.initial.etco2 && formatVital(vitals.initial.etco2) && (
                      <Stat size="sm">
                        <StatLabel fontSize="xs" color={labelColor}>EtCO₂</StatLabel>
                        <StatNumber fontSize="md">{formatVital(vitals.initial.etco2)}</StatNumber>
                      </Stat>
                    )}
                    {vitals.initial.pupils && hasValue(vitals.initial.pupils) && (
                      <Stat size="sm">
                        <StatLabel fontSize="xs" color={labelColor}>Pupils</StatLabel>
                        <StatNumber fontSize="sm" color={cardTextColor}>{vitals.initial.pupils}</StatNumber>
                      </Stat>
                    )}
                  </SimpleGrid>
                </Box>
              </CheckableItem>
              )}

              {/* 12-Lead ECG Interpretation - Only if included */}
              {vitals?.initial?.['12Lead'] && formatVital(vitals.initial['12Lead']) && (
                <Box mt={2}>
                  <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="xs">12-Lead ECG:</Text>
                  <Box bg="yellow.50" p={2} borderRadius="md" borderLeft="4px solid" borderColor="yellow.600">
                    <Text fontSize="xs" color="gray.800" whiteSpace="pre-wrap">{formatVital(vitals.initial['12Lead'])}</Text>
                  </Box>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Secondary Assessment / Physical Exam */}
        {secondaryAssessment && Object.keys(secondaryAssessment).some(key => hasValue(secondaryAssessment[key])) && (
          <Card bg={cardBg} shadow="md" color={cardTextColor} className="page-break-avoid">
            <CardHeader bg="cyan.600" color="white" borderTopRadius="md" py={1}>
              <Heading size="sm" color="white">Secondary Assessment</Heading>
            </CardHeader>
            <CardBody p={1}>
              <SimpleGrid columns={2} spacing={2}>
                {secondaryAssessment.heent && hasValue(secondaryAssessment.heent) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'heent')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'heent', checked)}
                    label="HEENT Exam"
                  >
                    <Box bg={boxBg} p={2} borderRadius="md">
                      <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">HEENT</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.heent}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.neck && hasValue(secondaryAssessment.neck) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'neck')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'neck', checked)}
                    label="Neck Exam"
                  >
                    <Box bg={boxBg} p={2} borderRadius="md">
                      <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Neck</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.neck}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.chest && hasValue(secondaryAssessment.chest) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'chest')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'chest', checked)}
                    label="Chest Exam"
                  >
                    <Box bg={boxBg} p={2} borderRadius="md">
                      <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Chest</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.chest}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.respiratory && hasValue(secondaryAssessment.respiratory) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'respiratory')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'respiratory', checked)}
                    label="Respiratory Exam"
                  >
                    <Box bg={boxBg} p={2} borderRadius="md">
                      <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Respiratory</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.respiratory}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.cardiovascular && hasValue(secondaryAssessment.cardiovascular) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'cardiovascular')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'cardiovascular', checked)}
                    label="Cardiovascular Exam"
                  >
                    <Box bg={boxBg} p={2} borderRadius="md">
                      <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Cardiovascular</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.cardiovascular}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.abdomen && hasValue(secondaryAssessment.abdomen) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'abdomen')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'abdomen', checked)}
                    label="Abdomen Exam"
                  >
                    <Box bg={boxBg} p={2} borderRadius="md">
                      <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Abdomen</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.abdomen}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.pelvis && hasValue(secondaryAssessment.pelvis) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'pelvis')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'pelvis', checked)}
                    label="Pelvis Exam"
                  >
                    <Box bg={boxBg} p={2} borderRadius="md">
                      <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Pelvis</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.pelvis}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.extremities && hasValue(secondaryAssessment.extremities) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'extremities')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'extremities', checked)}
                    label="Extremities Exam"
                  >
                    <Box bg={boxBg} p={2} borderRadius="md">
                      <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Extremities</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.extremities}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.back && hasValue(secondaryAssessment.back) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'back')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'back', checked)}
                    label="Back Exam"
                  >
                    <Box bg={boxBg} p={2} borderRadius="md">
                      <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Back</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.back}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.neuro && hasValue(secondaryAssessment.neuro) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'neuro')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'neuro', checked)}
                    label="Neurological Exam"
                  >
                    <Box bg={boxBg} p={2} borderRadius="md">
                      <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Neurological</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.neuro}</Text>
                    </Box>
                  </CheckableItem>
                )}

                {secondaryAssessment.skin && hasValue(secondaryAssessment.skin) && (
                  <CheckableItem
                    evaluationMode={evaluationMode}
                    isChecked={isChecked('secondary', 'skin')}
                    onToggle={(checked) => handleCheckboxToggle('secondary', 'skin', checked)}
                    label="Skin Exam"
                  >
                    <Box bg={boxBg} p={2} borderRadius="md">
                      <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Skin</Text>
                      <Text color={textColor} fontSize="sm">{secondaryAssessment.skin}</Text>
                    </Box>
                  </CheckableItem>
                )}
              </SimpleGrid>
            </CardBody>
          </Card>
        )}
      </SimpleGrid>

      {/* Row 4: Progression Vitals - 2 Columns */}
      {(scenario.vitalSigns?.progressionWithoutTreatment || scenario.vitalSigns?.progressionWithProperTreatment) && (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1} mb={1} className="trending-row-grid">
          {/* Progression WITH Proper Treatment */}
          {scenario.vitalSigns?.progressionWithProperTreatment && (
            <Card bg={trendingCardBg} shadow="md" color={cardTextColor}>
              <CardHeader bg="green.600" color="white" borderTopRadius="md" py={1}>
                <Heading size="sm" color="white">
                  <HStack><FaHeartbeat /><Text>Progression WITH Proper Treatment</Text></HStack>
                </Heading>
              </CardHeader>
              <CardBody p={1}>
                <VStack align="stretch" spacing={0}>
                  {scenario.vitalSigns.progressionWithProperTreatment.description && (
                    <Text fontSize="xs" fontWeight="semibold" color={textColor} mb={1}>
                      {scenario.vitalSigns.progressionWithProperTreatment.description}
                    </Text>
                  )}
                  {scenario.vitalSigns.progressionWithProperTreatment.timeframe && (
                    scenario.improvementVideoUrl ? (
                      <Badge 
                        colorScheme="green" 
                        mb={1} 
                        fontSize="xs" 
                        maxW="100%" 
                        wordBreak="break-word"
                        cursor="pointer"
                        onClick={() => openMediaModal('video', scenario.improvementVideoUrl, 'Patient Improvement Video')}
                        _hover={{ opacity: 0.8 }}
                      >
                        {scenario.vitalSigns.progressionWithProperTreatment.timeframe} 🎥
                      </Badge>
                    ) : (
                      <Badge colorScheme="green" mb={1} fontSize="xs" maxW="100%" wordBreak="break-word">
                        {scenario.vitalSigns.progressionWithProperTreatment.timeframe}
                      </Badge>
                    )
                  )}
                  {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals && (
                    <Text fontSize="xs" color={textColor} lineHeight="tall">
                      {[
                        scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.hr ? <><Text as="span" fontWeight="bold">HR</Text> {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.hr}</> : null,
                        scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.bp ? <><Text as="span" fontWeight="bold">BP</Text> {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.bp}</> : null,
                        scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.rr ? <><Text as="span" fontWeight="bold">RR</Text> {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.rr}</> : null,
                        scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.spo2 ? <><Text as="span" fontWeight="bold">SpO₂</Text> {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.spo2}</> : null,
                        scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.gcs ? <><Text as="span" fontWeight="bold">GCS</Text> {scenario.vitalSigns.progressionWithProperTreatment.expectedVitals.gcs}</> : null
                      ].filter(Boolean).map((part, i) => (
                        <Text as="span" key={i}>
                          {i > 0 && ' • '}
                          {part}
                        </Text>
                      ))}
                    </Text>
                  )}
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Deterioration Without Treatment/With Improper Treatment */}
          {scenario.vitalSigns?.progressionWithoutTreatment && (
            <Card bg={trendingCardBg} shadow="md" color={cardTextColor}>
              <CardHeader bg="red.600" color="white" borderTopRadius="md" py={1}>
                <Heading size="sm" color="white">
                  <HStack><FaExclamationTriangle /><Text>Deterioration Without Treatment/With Improper Treatment</Text></HStack>
                </Heading>
              </CardHeader>
              <CardBody p={1} overflow="hidden">
                <VStack align="stretch" spacing={0}>
                  {scenario.vitalSigns.progressionWithoutTreatment.description && (
                    <Box overflow="hidden" p={1}>
                      <Text fontSize="xs" fontWeight="semibold" color={textColor} mb={1} whiteSpace="pre-wrap" wordBreak="break-word" overflowWrap="break-word">
                        {scenario.vitalSigns.progressionWithoutTreatment.description}
                      </Text>
                    </Box>
                  )}
                  {scenario.vitalSigns.progressionWithoutTreatment.timeframe && (
                    scenario.deteriorationVideoUrl ? (
                      <Badge 
                        colorScheme="red" 
                        mb={1} 
                        fontSize="xs" 
                        maxW="100%" 
                        wordBreak="break-word"
                        cursor="pointer"
                        onClick={() => openMediaModal('video', scenario.deteriorationVideoUrl, 'Patient Deterioration Video')}
                        _hover={{ opacity: 0.8 }}
                      >
                        {scenario.vitalSigns.progressionWithoutTreatment.timeframe} 🎥
                      </Badge>
                    ) : (
                      <Badge colorScheme="red" mb={1} fontSize="xs" maxW="100%" wordBreak="break-word">
                        {scenario.vitalSigns.progressionWithoutTreatment.timeframe}
                      </Badge>
                    )
                  )}
                  {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals && (
                    <Text fontSize="xs" color={textColor} lineHeight="tall" whiteSpace="pre-wrap" wordBreak="break-word">
                      {[
                        scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.hr ? <><Text as="span" fontWeight="bold">HR</Text> {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.hr}</> : null,
                        scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.bp ? <><Text as="span" fontWeight="bold">BP</Text> {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.bp}</> : null,
                        scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.rr ? <><Text as="span" fontWeight="bold">RR</Text> {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.rr}</> : null,
                        scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.spo2 ? <><Text as="span" fontWeight="bold">SpO₂</Text> {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.spo2}</> : null,
                        scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.etco2 ? <><Text as="span" fontWeight="bold">EtCO₂</Text> {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.etco2}</> : null,
                        scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.gcs ? <><Text as="span" fontWeight="bold">GCS</Text> {scenario.vitalSigns.progressionWithoutTreatment.expectedVitals.gcs}</> : null
                      ].filter(Boolean).map((part, i) => (
                        <Text as="span" key={i}>
                          {i > 0 && ' • '}
                          {part}
                        </Text>
                      ))}
                    </Text>
                  )}
                  
                  {/* Cardiac Arrest Sequence - Compact subsection */}
                  {hasCardiacArrest && (
                    <Box mt={2} pt={2} borderTop="1px solid" borderColor="red.300">
                      <Text fontSize="2xs" fontWeight="bold" color="red.700" mb={0.5}>If Cardiac Arrest Occurs:</Text>
                      <Table size="sm" variant="simple">
                        <Tbody>
                          {cardiacArrest.initialRhythm && hasValue(cardiacArrest.initialRhythm) && (
                            <Tr>
                              <Td fontSize="2xs" p={0.5} fontWeight="semibold">Initial:</Td>
                              <Td fontSize="2xs" p={0.5}>{cardiacArrest.initialRhythm}</Td>
                            </Tr>
                          )}
                          {cardiacArrest.sequence && cardiacArrest.sequence.length > 0 && cardiacArrest.sequence.map((step, index) => (
                            <Tr key={index}>
                              <Td fontSize="2xs" p={0.5} fontWeight="semibold">{index + 1}.</Td>
                              <Td fontSize="2xs" p={0.5}>
                                {typeof step === 'string' ? step : (
                                  `${step.rhythm || 'N/A'} - ${step.intervention || 'N/A'}${step.expectedResponse ? ` (${step.expectedResponse})` : ''}`
                                )}
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                      {cardiacArrest.targetOutcome && hasValue(cardiacArrest.targetOutcome) && (
                        <Text fontSize="2xs" color={textColor} mt={0.5} fontStyle="italic">→ {cardiacArrest.targetOutcome}</Text>
                      )}
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>
          )}
        </SimpleGrid>
      )}

      {/* Interventions - Categorized - 2 Column Layout */}
      {interventions && (interventions.LifeSaving?.length > 0 || interventions.Secondary?.length > 0 || interventions.Optional?.length > 0) && (
        <Card mb={1.5} bg={cardBg} shadow="md" color={cardTextColor} className="page-break-avoid interventions-card">
          <CardHeader bg="orange.600" color="white" borderTopRadius="md" py={1.5}>
            <Heading size="sm" color="white">Interventions</Heading>
          </CardHeader>
          <CardBody p={1.5}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1.5}>
              {/* Life-Saving Interventions */}
              {interventions.LifeSaving && interventions.LifeSaving.length > 0 && (
                <Box>
                  <Text fontWeight="bold" color="red.700" mb={1} fontSize="sm">Critical / Life-Saving</Text>
                  <VStack align="stretch" spacing={1} className="interventions-grid">
                    {interventions.LifeSaving.map((intervention, index) => (
                      <CheckableItem
                        key={makeChecklistKey("interventions_lifesaving", intervention, index)}
                        evaluationMode={evaluationMode}
                        isChecked={isChecked("interventions", makeChecklistKey("lifesaving", intervention, index))}
                        onToggle={(checked) => handleCheckboxToggle("interventions", makeChecklistKey("lifesaving", intervention, index), checked)}
                        label={intervention}
                      >
                        <Card bg="red.50" borderLeft="4px solid" borderColor="red.600">
                          <CardBody py={1} px={2}>
                            <HStack spacing={1}>
                              <FaExclamationTriangle color="#C53030" size={12} />
                              <Text fontWeight="semibold" color="red.900" fontSize="xs">{intervention}</Text>
                            </HStack>
                          </CardBody>
                        </Card>
                      </CheckableItem>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* Secondary + Optional Interventions Combined */}
              <VStack align="stretch" spacing={1.5}>
                {/* Secondary Interventions */}
                {interventions.Secondary && interventions.Secondary.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Secondary</Text>
                    <VStack align="stretch" spacing={1} className="interventions-grid">
                      {interventions.Secondary.map((intervention, index) => (
                        <CheckableItem
                          key={makeChecklistKey("interventions_secondary", intervention, index)}
                          evaluationMode={evaluationMode}
                          isChecked={isChecked("interventions", makeChecklistKey("secondary", intervention, index))}
                          onToggle={(checked) => handleCheckboxToggle("interventions", makeChecklistKey("secondary", intervention, index), checked)}
                          label={intervention}
                        >
                          <Card bg="blue.50" borderLeft="4px solid" borderColor="blue.500">
                            <CardBody py={1} px={2}>
                              <HStack spacing={1}>
                                <FaCheckCircle color="#3182CE" size={12} />
                                <Text color="blue.900" fontSize="xs">{intervention}</Text>
                              </HStack>
                            </CardBody>
                          </Card>
                        </CheckableItem>
                      ))}
                    </VStack>
                  </Box>
                )}

                {/* Optional Interventions */}
                {interventions.Optional && interventions.Optional.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" color={boldTextColor} mb={1} fontSize="sm">Optional/Conditional</Text>
                    <VStack align="stretch" spacing={1} className="interventions-grid">
                      {interventions.Optional.map((intervention, index) => (
                        <CheckableItem
                          key={makeChecklistKey("interventions_optional", intervention, index)}
                          evaluationMode={evaluationMode}
                          isChecked={isChecked("interventions", makeChecklistKey("optional", intervention, index))}
                          onToggle={(checked) => handleCheckboxToggle("interventions", makeChecklistKey("optional", intervention, index), checked)}
                          label={intervention}
                        >
                          <Card bg="gray.50" borderLeft="4px solid" borderColor="gray.400">
                            <CardBody py={1} px={2}>
                              <Text color="gray.700" fontSize="xs">{intervention}</Text>
                            </CardBody>
                          </Card>
                        </CheckableItem>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Trending Vital Signs */}
      {vitals.trending && vitals.trending.length > 0 && (
        <Card bg={cardBg} shadow="md" color={cardTextColor} mb={4}>
          <CardHeader bg="pink.600" color="white" borderTopRadius="md" py={1}>
            <Heading size="sm" color="white">Trending Vitals</Heading>
          </CardHeader>
          <CardBody p={2}>
            <VStack align="stretch" spacing={0.5} fontSize="xs">
              {vitals.trending.map((vitalSet, index) => {
                const vitalsParts = [];
                if (vitalSet.hr && formatVital(vitalSet.hr)) vitalsParts.push(<><Text as="span" fontWeight="bold">HR</Text> {formatVital(vitalSet.hr)}</>);
                if (vitalSet.bp && formatVital(vitalSet.bp)) vitalsParts.push(<><Text as="span" fontWeight="bold">BP</Text> {formatVital(vitalSet.bp)}</>);
                if (vitalSet.rr && formatVital(vitalSet.rr)) vitalsParts.push(<><Text as="span" fontWeight="bold">RR</Text> {formatVital(vitalSet.rr)}</>);
                if (vitalSet.spo2 && formatVital(vitalSet.spo2)) vitalsParts.push(<><Text as="span" fontWeight="bold">SpO₂</Text> {formatVital(vitalSet.spo2)}</>);
                if (vitalSet.temp && formatVital(vitalSet.temp)) vitalsParts.push(<><Text as="span" fontWeight="bold">T</Text> {formatVital(vitalSet.temp)}</>);
                if (vitalSet.bloodGlucose && formatVital(vitalSet.bloodGlucose)) vitalsParts.push(<><Text as="span" fontWeight="bold">BGL</Text> {formatVital(vitalSet.bloodGlucose)}</>);
                if (vitalSet.etco2 && formatVital(vitalSet.etco2)) vitalsParts.push(<><Text as="span" fontWeight="bold">EtCO₂</Text> {formatVital(vitalSet.etco2)}</>);
                
                return (
                  <Box key={index} borderBottom="1px solid" borderColor="gray.200" py={1}>
                    <Text color={textColor}>
                      <Text as="span" fontWeight="bold" color="pink.700">
                        • {vitalSet.time && hasValue(vitalSet.time) ? vitalSet.time : `Time ${index + 1}`}:
                      </Text>{' '}
                      {vitalsParts.map((part, i) => (
                        <Text as="span" key={i}>
                          {i > 0 && ' • '}
                          {part}
                        </Text>
                      ))}
                    </Text>
                    {vitalSet.notes && hasValue(vitalSet.notes) && (
                      <Text fontSize="2xs" color={labelColor} fontStyle="italic" mt={0.5}>
                        {vitalSet.notes}
                      </Text>
                    )}
                  </Box>
                );
              })}
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Expected Outcome */}
      {(outcome.finalCondition || outcome.disposition || outcome.continuingCareNeeds) && (
        <Card bg={cardBg} shadow="md" color={cardTextColor} mb={4}>
          <CardHeader bg="blue.600" color="white" borderTopRadius="md">
            <Heading size="md" color="white">Expected Outcome</Heading>
          </CardHeader>
          <CardBody>
            {outcome.finalCondition && hasValue(outcome.finalCondition) && (
              <Text mb={2}><Text as="span" fontWeight="bold">Final Condition:</Text> {outcome.finalCondition}</Text>
            )}
            {outcome.disposition && hasValue(outcome.disposition) && (
              <Text mb={2}><Text as="span" fontWeight="bold">Disposition:</Text> {outcome.disposition}</Text>
            )}
            {outcome.continuingCareNeeds && hasValue(outcome.continuingCareNeeds) && (
              <Text><Text as="span" fontWeight="bold">Continuing Care Needs:</Text> {outcome.continuingCareNeeds}</Text>
            )}
          </CardBody>
        </Card>
      )}

      {/* Critical Actions and Common Pitfalls - Side by Side */}
      {((learning.criticalActions && learning.criticalActions.length > 0) || (learning.commonPitfalls && learning.commonPitfalls.length > 0)) && (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4} className="page-break-avoid">
          {/* Critical Actions */}
          {learning.criticalActions && learning.criticalActions.length > 0 && (
            <Card bg={cardBg} shadow="md" color={cardTextColor}>
              <CardHeader bg="red.600" color="white" borderTopRadius="md">
                <HStack>
                  <FaExclamationTriangle />
                  <Heading size="md" color="white">Critical Actions</Heading>
                </HStack>
              </CardHeader>
              <CardBody>
                <List spacing={1} className="critical-actions-list">
                  {learning.criticalActions.map((action, index) => (
                    <CheckableItem
                      key={makeChecklistKey("critical_actions", action, index)}
                      evaluationMode={evaluationMode}
                      isChecked={isChecked("criticalActions", makeChecklistKey("action", action, index))}
                      onToggle={(checked) => handleCheckboxToggle("criticalActions", makeChecklistKey("action", action, index), checked)}
                      label={action}
                    >
                      <ListItem>
                        <HStack align="start">
                          <ListIcon as={FaExclamationTriangle} color="red.500" mt={1} />
                          <Text color={textColor} fontWeight="semibold">{action}</Text>
                        </HStack>
                      </ListItem>
                    </CheckableItem>
                  ))}
                </List>
              </CardBody>
            </Card>
          )}

          {/* Common Pitfalls */}
          {learning.commonPitfalls && learning.commonPitfalls.length > 0 && (
            <Card bg={cardBg} shadow="md" color={cardTextColor}>
              <CardHeader bg="orange.600" color="white" borderTopRadius="md">
                <HStack>
                  <FaExclamationTriangle />
                  <Heading size="md" color="white">Common Pitfalls</Heading>
                </HStack>
              </CardHeader>
              <CardBody>
                <List spacing={1} className="common-pitfalls-list">
                  {learning.commonPitfalls.map((pitfall, index) => (
                    <CheckableItem
                      key={makeChecklistKey("pitfalls", pitfall, index)}
                      evaluationMode={evaluationMode}
                      isChecked={isChecked("pitfalls", makeChecklistKey("pitfall", pitfall, index))}
                      onToggle={(checked) => handleCheckboxToggle("pitfalls", makeChecklistKey("pitfall", pitfall, index), checked)}
                      label={pitfall}
                    >
                      <ListItem>
                        <HStack align="start">
                          <ListIcon as={FaExclamationTriangle} color="orange.500" mt={1} />
                          <Text color={textColor}>{pitfall}</Text>
                        </HStack>
                      </ListItem>
                    </CheckableItem>
                  ))}
                </List>
              </CardBody>
            </Card>
          )}
        </SimpleGrid>
      )}

      {/* Hard Page Break - Differentials and Definitive Care on Page 2 */}
      <Box className={`page-break-before-differentials page-break-after ${shouldForcePageBreak ? 'force-page-break' : ''}`} />

      {/* Differential Diagnosis and Definitive Care - Side by Side */}
      {(learning.differentialDiagnosis && learning.differentialDiagnosis.length > 0) || hasDefinitiveCare ? (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4} className="page-break-avoid">
          {/* Differential Diagnosis - Left Half */}
          {learning.differentialDiagnosis && learning.differentialDiagnosis.length > 0 && (
            <Card bg={cardBg} shadow="md" color={cardTextColor}>
              <CardHeader bg="cyan.600" color="white" borderTopRadius="md" py={1}>
                <HStack>
                  <FaBrain />
                  <Heading size="sm" color="white">Differential Diagnosis</Heading>
                </HStack>
              </CardHeader>
              <CardBody p={2}>
                <VStack align="stretch" spacing={3}>
                  {learning.differentialDiagnosis.map((diagnosis, index) => {
                    // Handle both string format (legacy) and object format (new)
                    if (typeof diagnosis === 'string') {
                      return (
                        <HStack key={index} align="start">
                          <Icon as={FaCheckCircle} color="cyan.500" mt={1} />
                          <Text color={textColor}>{diagnosis}</Text>
                        </HStack>
                      );
                    } else {
                      return (
                        <Box key={index} bg={boxBg} p={3} borderRadius="md" borderLeft="4px solid" borderColor="cyan.500">
                          <HStack mb={1}>
                            <Text fontWeight="bold" color={boldTextColor} fontSize="md">
                              {diagnosis.condition || diagnosis.Condition}
                            </Text>
                            {(diagnosis.likelihood || diagnosis.Likelihood) && (
                              <Badge colorScheme="cyan" fontSize="xs">
                                {diagnosis.likelihood || diagnosis.Likelihood}
                              </Badge>
                            )}
                          </HStack>
                          {(diagnosis.supportingFindings || diagnosis.SupportingFindings) && (
                            <Text color="green.600" fontSize="sm" mt={1}>
                              ✓ Supporting: {diagnosis.supportingFindings || diagnosis.SupportingFindings}
                            </Text>
                          )}
                          {(diagnosis.contradictingFindings || diagnosis.ContradictingFindings) && (
                            <Text color="red.600" fontSize="sm" mt={1}>
                              ✗ Against: {diagnosis.contradictingFindings || diagnosis.ContradictingFindings}
                            </Text>
                          )}
                        </Box>
                      );
                    }
                  })}
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Definitive Care - Enhanced Display */}
          {hasDefinitiveCare && (
            <Card bg={cardBg} shadow="md" color={cardTextColor} className="avoid-break">
              <CardHeader bg="teal.600" color="white" borderTopRadius="md" py={1}>
                <HStack>
                  <FaHospital />
                  <Heading size="sm" color="white">Hospital Definitive Care</Heading>
                </HStack>
              </CardHeader>
              <CardBody p={2}>
                <VStack spacing={2} align="stretch">
                  {/* Lab Tests with Values */}
                  {definitiveCare.labTests && definitiveCare.labTests.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color={labelColor} mb={1}>Laboratory Results</Text>
                      <List spacing={0.5}>
                        {definitiveCare.labTests.map((test, index) => (
                          <ListItem key={index} fontSize="sm" color={textColor}>• {test}</ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {/* Imaging with Detailed Findings */}
                  {definitiveCare.imagingStudies && definitiveCare.imagingStudies.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color={labelColor} mb={1}>Imaging Studies</Text>
                      <List spacing={0.5}>
                        {definitiveCare.imagingStudies.map((study, index) => (
                          <ListItem key={index} fontSize="sm" color={textColor}>• {study}</ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {/* Diagnostic Procedures */}
                  {definitiveCare.diagnosticProcedures && definitiveCare.diagnosticProcedures.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color={labelColor} mb={1}>Diagnostic Procedures</Text>
                      <List spacing={0.5}>
                        {definitiveCare.diagnosticProcedures.map((procedure, index) => (
                          <ListItem key={index} fontSize="sm" color={textColor}>• {procedure}</ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {/* Definitive Interventions - What Actually Fixes the Problem */}
                  {definitiveCare.definitiveInterventions && definitiveCare.definitiveInterventions.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color={labelColor} mb={1}>Definitive Treatment (What Fixes the Problem)</Text>
                      <List spacing={0.5}>
                        {definitiveCare.definitiveInterventions.map((intervention, index) => (
                          <ListItem key={index} fontSize="sm" color={textColor} fontWeight="medium">• {intervention}</ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>
          )}
        </SimpleGrid>
      ) : null}

      {/* Evaluation Form - Page 2 for print/tablet modes */}
      {evaluationForm && (
        <>
          {/* Page break before evaluation */}
          <Box className="page-break-before" />
          
          <Card bg={cardBg} shadow="md" color={cardTextColor} className="page-break-avoid">
            <CardHeader bg="teal.600" color="white" borderTopRadius="md">
              <Heading size="md" color="white">Evaluation Form</Heading>
            </CardHeader>
            <CardBody>
              {evaluationForm}
            </CardBody>
          </Card>
        </>
      )}

      </Box>
        </>
      )}



      {/* Watermarks - always visible to prevent screenshots */}
      {watermarkConfig && scenario && user && (
        <>
          {/* Overt watermark */}
          <Box
            className="scenario-watermark"
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            pointerEvents="none"
            userSelect="none"
          >
            {renderOvertWatermark()}
          </Box>
          
          {/* Subtle watermark */}
          {renderSubtleWatermark()}
        </>
      )}

      {/* Media Viewing Modal */}
      <Modal isOpen={mediaModalOpen} onClose={() => setMediaModalOpen(false)} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{currentMedia?.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* AI Generated Disclaimer */}
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box flex="1">
                  <AlertTitle fontSize="sm">AI-Generated Content</AlertTitle>
                  <AlertDescription fontSize="xs">
                    This media was generated by artificial intelligence and may not be medically accurate. 
                    It is for educational purposes only. Any concerns should be reported to{' '}
                    <Text as="a" href="mailto:support@elevationems.com" color="blue.500" textDecoration="underline">
                      support@elevationems.com
                    </Text>
                  </AlertDescription>
                </Box>
              </Alert>
              
              {/* Media Display */}
              {currentMedia?.type === 'image' && (
                <AspectRatio ratio={16/9}>
                  <Image 
                    src={currentMedia.url} 
                    alt={currentMedia.title}
                    borderRadius="md"
                    objectFit="contain"
                  />
                </AspectRatio>
              )}
              
              {currentMedia?.type === 'video' && (
                <AspectRatio ratio={16/9}>
                  <video 
                    controls 
                    autoPlay
                    style={{ borderRadius: '8px' }}
                    src={currentMedia.url}
                  >
                    Your browser does not support the video tag.
                  </video>
                </AspectRatio>
              )}
              
              <HStack justify="flex-end">
                <Button
                  as="a"
                  href={currentMedia?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  leftIcon={<ExternalLinkIcon />}
                  colorScheme="blue"
                  size="sm"
                >
                  Open in New Tab
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setMediaModalOpen(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ScenarioDisplay;
